import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { parsePyMolCommands } from './pymolParser'
import { APIContact } from '../../helper/APIFunctions'
import moleculeBackground from '../../assets/molecule_background.png'
import { softRandomUUID } from "../../helper/Utils"
import {APIWebVisualization} from '../../helper/APIFunctions'
import YouTubeVideoPopup from '../YouTubeVideoPopup'

type ChatMessage = { role: 'user' | 'assistant' | 'system'; content: string }

type ParsedObject = {
  source: 'pdb' | 'pubchem'
  id: string
  targetName: string
  position?: [number, number, number]
  scale?: [number, number, number]
  style?: Record<string, unknown>
}

type ParseResponse = {
  objects: ParsedObject[]
  globalViz?: { background?: string }
  notes?: string
  updates?: any[] // New field for backend updates
  clarify?: string // New field for backend clarifications
  assistant?: string // New field for backend assistant messages
  actions?: any[] // New field for backend actions (like clear)
}

const VIEWER_URL = 'https://vis.snorkle.io/resources/multiscale_viewer/20260116/index.html'
const BACKEND_URL = process.env.REACT_APP_MOLECULAR_VIEWER_API_ENDPOINT || 'http://localhost:8000'
const NORMALIZED_MODEL_RADIUS = 1.5;

const CloseButton: React.FC<{ onClick: () => void, windowName: string }> = ({ onClick, windowName }) => {
  const CLOSE_BUTTON_COLOR = 'rgba(233, 42, 42, 0.4)';
  const CLOSE_BUTTON_HOVER_COLOR = 'rgba(233, 42, 42, 0.8)';
  return (
    <button
      onClick={onClick}
      style={{borderRadius: "50%", 
        border: '1px solid rgba(99,102,241,0.2)',
         background: CLOSE_BUTTON_COLOR,
          color: 'white', 
          cursor: 'pointer',
           fontSize: 20,
            transition: 'all 200ms cubic-bezier(0.4, 0, 0.2, 1)',
             position: "absolute",
              top:0,
              left:0,
              transform: "translate(-50%, -50%)",
              height: "1em",
              width: "1em",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
             }}
      onMouseEnter={(e) => { e.currentTarget.style.background = CLOSE_BUTTON_HOVER_COLOR; e.currentTarget.style.borderColor = 'rgba(99,102,241,0.35)' }}
      onMouseLeave={(e) => { e.currentTarget.style.background = CLOSE_BUTTON_COLOR; e.currentTarget.style.borderColor = 'rgba(99,102,241,0.2)' }}
      title = {`Close ${windowName}`}
    >
      ×
    </button>
  )
}


const MolecularViewerApp: React.FC = () => {
  // Log backend URL configuration on component mount
  useEffect(() => {
  }, [])
  
  const iframeRef = useRef<HTMLIFrameElement | null>(null)
  const pendingMapRef = useRef<Map<string, (payload: any) => void>>(new Map())
  const viewerReadyRef = useRef<boolean>(false)
  const [loaded, setLoaded] = useState(false)
  const [messages, setMessages] = useState<ChatMessage[]>([])
  const [input, setInput] = useState('')
  const [working, setWorking] = useState(false)
  const [showOverlay, setShowOverlay] = useState<boolean>(false)
  const [overlayText, setOverlayText] = useState<string>('')
  const [backendLogs, setBackendLogs] = useState<string[]>([])
  const lastBackendLogCountRef = useRef<number>(0)
  const [disableSaveView, setDisableSaveView] = useState<boolean>(false)
  const messagesContainerRef = useRef<HTMLDivElement | null>(null)
  const persistentMapRef = useRef<Map<string, (payload: any) => void>>(new Map())
  const currentAssetNameRef = useRef<string>('PDBAsset')
  const lastSentSourceModeRef = useRef<'auto'|'pdb'|'pubchem'>('auto')
  const [sourceMode, setSourceMode] = useState<'auto'|'pdb'|'pubchem'>(() => {
    const saved = typeof localStorage !== 'undefined' ? localStorage.getItem('vibe.sourceMode') : null
    return (saved === 'pdb' || saved === 'pubchem' || saved === 'auto') ? (saved as any) : 'auto'
  })
  const [suggestions, setSuggestions] = useState<Array<{ label: string; props: Record<string, any> }>>([])
  const [showOptions, setShowOptions] = useState<boolean>(false)
  const [showSequence, setShowSequence] = useState<boolean>(false)
  const [showWorkspace, setShowWorkspace] = useState<boolean>(false)
  const [currentInfo, setCurrentInfo] = useState<any>(null)
  const [currentProps, setCurrentProps] = useState<Record<string, any>>({})
  const [pymolMode, setPymolMode] = useState<boolean>(false)
  const [showPymolHelp, setShowPymolHelp] = useState<boolean>(false)
  const [pendingProps, setPendingProps] = useState<Record<string, any>>({})
  const [lastClickInfo, setLastClickInfo] = useState<any>(null)
  const [awaitingClearConfirm, setAwaitingClearConfirm] = useState<boolean>(false)
  const clickSubscribedRef = useRef<boolean>(false)
  const [hoverInfo, setHoverInfo] = useState<any>(null)
  const viewerContainerRef = useRef<HTMLDivElement | null>(null)
  const lastPointerRef = useRef<{ x: number; y: number } | null>(null)
  const [clickPos, setClickPos] = useState<{ x: number; y: number } | null>(null)
  const [structure, setStructure] = useState<any[] | null>(null)
  const [chainIdx, setChainIdx] = useState<number>(0)
  const [hoveredResidueId, setHoveredResidueId] = useState<number | null>(null) // Track hovered residue from molecule
  const [showSettingsBtn, setShowSettingsBtn] = useState<boolean>(false)
  const settingsHideTimerRef = useRef<any>(null)
  const [showSettingsMenu, setShowSettingsMenu] = useState<boolean>(false)
  const activeLabelNameRef = useRef<string>('ClickLabel')
  const backgroundClickTimerRef = useRef<any>(null)
  const rotationPeriodRef = useRef<number>(0)
  const iframeClickDetectedRef = useRef<boolean>(false)
  const [showPromptPanel, setShowPromptPanel] = useState<boolean>(false) // Whether AI panel is open
  const [showHistory, setShowHistory] = useState<boolean>(true) // Whether to show chat history
  const [chatButtonRotation, setChatButtonRotation] = useState<number>(0) // Track chat button rotation for hover
  const [introComplete, setIntroComplete] = useState<boolean>(false) // Track intro animation
  const [loadedAssets, setLoadedAssets] = useState<Array<{ source: string; id: string }>>([]) // Track currently loaded assets
  const [activeMolecule, setActiveMolecule] = useState<string | null>(null) // Track which molecule is currently active
  const [firstTimeUser, setFirstTimeUser] = useState<boolean>(true) // Track if user has interacted yet
  const [showSettingsFlash, setShowSettingsFlash] = useState<boolean>(false) // Flash settings button on molecule load
  const [sceneState, setSceneState] = useState<any>(null) // Comprehensive scene state from viewer
  const prevAssetCountRef = useRef<number>(0) // Track previous asset count to detect additions (not deletions)
  const [commandHistory, setCommandHistory] = useState<string[]>([]) // Command history for arrow key navigation
  const [historyIndex, setHistoryIndex] = useState<number>(-1) // Current position in history (-1 = not navigating)
  const sidebarInputRef = useRef<HTMLInputElement | null>(null) // Ref for sidebar input field
  const welcomeInputRef = useRef<HTMLInputElement | null>(null) // Ref for welcome screen input field
  const isOpeningPanelRef = useRef<boolean>(false) // Track if we're in the process of opening the panel
  const [showReportModal, setShowReportModal] = useState<boolean>(false) // Report inaccuracies modal
  const [reportComment, setReportComment] = useState<string>('') // User's optional comment
  const [reportSubmitting, setReportSubmitting] = useState<boolean>(false) // Submission state
  const [reportStatus, setReportStatus] = useState<{ success?: boolean; error?: string }>({}) // Submission result
  const [hoveredTooltip, setHoveredTooltip] = useState<string | null>(null) // Track which tooltip is being hovered
  const [showDeleteConfirm, setShowDeleteConfirm] = useState<boolean>(false) // Delete molecule confirmation modal
  const [moleculeToDelete, setMoleculeToDelete] = useState<string | null>(null) // Molecule name pending deletion
  const lastMoleculeClickTimeRef = useRef<number>(0) // Track when user last clicked a molecule
  const DELETE_INTERACTION_WINDOW = 3000 // 3 seconds after clicking molecule, Delete key will work
  const [webglError, setWebglError] = useState<boolean>(false) // Track WebGL initialization failures

  const headerHeight = () => {
    const navbar = document.getElementsByClassName('navbar')[0];
    return navbar?.clientHeight || 0
  };

  // Auto-focus input when prompt panel opens
  useEffect(() => {
    if (showPromptPanel && sidebarInputRef.current) {
      sidebarInputRef.current.focus()
    }
  }, [showPromptPanel])

  // Global keyboard handler: open prompt panel when typing
  useEffect(() => {
    const handleGlobalKeyDown = (e: KeyboardEvent) => {
      // Don't trigger if prompt panel is already open or opening
      if (showPromptPanel || isOpeningPanelRef.current) return
      
      // Don't trigger if user is typing in an input/textarea
      const target = e.target as HTMLElement
      if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA') return
      
      // Don't trigger for special keys
      const specialKeys = [
        'Escape', 'Tab', 'Enter', 'Shift', 'Control', 'Alt', 'Meta',
        'ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight',
        'Backspace', 'Delete', 'Home', 'End', 'PageUp', 'PageDown',
        'F1', 'F2', 'F3', 'F4', 'F5', 'F6', 'F7', 'F8', 'F9', 'F10', 'F11', 'F12'
      ]
      
      if (specialKeys.includes(e.key)) return
      
      // Don't trigger if modifier keys are pressed (except Shift for capital letters)
      if (e.ctrlKey || e.metaKey || e.altKey) return
      
      // Only trigger for printable characters (single character keys)
      if (e.key.length === 1) {
        e.preventDefault()
        isOpeningPanelRef.current = true // Immediately block further global captures
        setInput(e.key)
        setShowPromptPanel(true)
        
        // Focus will happen after panel renders - poll until ref is available
        const attemptFocus = (attempts = 0) => {
          if (sidebarInputRef.current) {
            sidebarInputRef.current.focus()
            // Move cursor to end of input
            sidebarInputRef.current.setSelectionRange(e.key.length, e.key.length)
            // Reset the flag after input is focused
            setTimeout(() => {
              isOpeningPanelRef.current = false
            }, 50)
          } else if (attempts < 20) {
            // Try again in next frame
            requestAnimationFrame(() => attemptFocus(attempts + 1))
          } else {
            isOpeningPanelRef.current = false
          }
        }
        
        // Start attempting to focus on next frame
        requestAnimationFrame(() => attemptFocus(0))
      }
    }
    
    window.addEventListener('keydown', handleGlobalKeyDown)
    return () => window.removeEventListener('keydown', handleGlobalKeyDown)
  }, [showPromptPanel])

  useEffect(() => {
    const onMessage = (event: MessageEvent) => {
      const data: any = event.data
      if (!data || typeof data !== 'object') return
      const id = data.id as string | undefined
      if (!id) return
      
      // Filter logging to important messages only
      const importantMessages = ['AddAssets', 'AssetsAdded', 'GetComponents', 'GetCameraPosition']
      const shouldLog = importantMessages.includes(data.description)
      
      if (data.errors && data.errors.length) {
        const desc = data.description
        const errs = Array.isArray(data.errors) ? data.errors.join(' | ') : String(data.errors)
        const isProbeInvalid = desc === 'GetComponentProperties' && errs.includes('Invalid target')
        const isSelectionInvalid = desc === 'SetSelection' && errs.includes('Invalid selection target')
        const isClickLabelMissing = desc === 'RemoveAsset' && errs.includes('ClickLabel')
        if (!isProbeInvalid && !isSelectionInvalid && !isClickLabelMissing && shouldLog) {
        }
      } else if (shouldLog) {
      }
      const handler = pendingMapRef.current.get(id)
      if (handler) {
        // Ignore interim status messages like "working" and keep the handler installed
        if (data && data.result === 'working') {
          return
        }
        pendingMapRef.current.delete(id)
        handler(data)
      }
      // Persistent subscriptions (do not delete handler)
      const ph = persistentMapRef.current.get(id)
      if (ph) {
        ph(data)
      }
    }
    window.addEventListener('message', onMessage)
    return () => window.removeEventListener('message', onMessage)
  }, [])

  // Detect WebGL errors from iframe
  useEffect(() => {
    const handleError = (event: ErrorEvent) => {
      const errorMsg = event.message || event.error?.message || '';
      if (errorMsg.includes('WebGL') || errorMsg.includes('webgl')) {
        setWebglError(true);
      }
    };
    
    // Listen for global error events
    window.addEventListener('error', handleError);
    
    // Check if iframe loads but doesn't respond (WebGL failed silently)
    const checkTimeout = setTimeout(() => {
      if (loaded && !viewerReadyRef.current) {
        // Iframe loaded but viewer never became ready - likely WebGL failure
        setWebglError(true);
      }
    }, 5000); // Give it 5 seconds to initialize
    
    return () => {
      window.removeEventListener('error', handleError);
      clearTimeout(checkTimeout);
    };
  }, [loaded]);

  // Refresh overlay when source mode changes so the displayed mode updates immediately
  useEffect(() => {
    const run = async () => { try { await refreshOverlay() } catch {} }
    // Defer until after refreshOverlay is defined
    // eslint-disable-next-line @typescript-eslint/no-misused-promises
    setTimeout(run, 0)
  }, [sourceMode])

  // Persist source mode
  useEffect(() => {
    try { localStorage.setItem('vibe.sourceMode', sourceMode) } catch {}
  }, [sourceMode])

  // Poll backend logs periodically (DISABLED for performance)
  // useEffect(() => {
  //   let timer: any
  //   const tick = async () => {
  //     try {
  //       const res = await fetch(`${BACKEND_URL}/logs?limit=200`)
  //       if (res.ok) {
  //         const data = await res.json()
  //         if (Array.isArray(data?.lines)) {
  //           const logs = data.lines as string[]
  //           // stream only new lines to console
  //           let last = lastBackendLogCountRef.current
  //           if (last > logs.length) last = 0
  //           for (let i = last; i < logs.length; i++) {
  //             // eslint-disable-next-line no-console
  //           }
  //           lastBackendLogCountRef.current = logs.length
  //           setBackendLogs(logs)
  //         }
  //       }
  //     } catch {}
  //     timer = setTimeout(tick, 1500)
  //   }
  //   tick()
  //   return () => { if (timer) clearTimeout(timer) }
  // }, [])

  // Auto-scroll chat to bottom on new messages
  useEffect(() => {
    const el = messagesContainerRef.current
    if (el) {
      el.scrollTop = el.scrollHeight
    }
  }, [messages])

  const sendMessage = useCallback((message: Record<string, any>) => {
    const id = softRandomUUID()
    const m: any = message
    // Only log important messages (skip click/hover/selection noise)
    const importantMessages = ['AddAssets', 'RemoveAsset', 'GetComponents']
    if (importantMessages.includes(m?.description)) {
    }
    if (m?.description === 'SetAutoOrbit') {
      rotationPeriodRef.current = m?.result?.period ?? 0;
    }
    const payload = (message && typeof message === 'object') ? { id, ...message } : { id }
    iframeRef.current?.contentWindow?.postMessage(payload, '*')
  }, [])

  const sendAndWait = useCallback((message: Record<string, any>): Promise<{ result?: any; errors?: any }> => {
    return new Promise((resolve) => {
      const id = softRandomUUID()
      const m: any = message
      // Only log important messages (skip click/hover/selection noise)
      const importantMessages = ['AddAssets', 'GetComponents', 'GetCameraPosition']
      if (importantMessages.includes(m?.description)) {
      }
      if (m?.description === 'SetAutoOrbit') {
        rotationPeriodRef.current = m?.result?.period ?? 0;
      }
      pendingMapRef.current.set(id, (payload) => {
        resolve({ result: payload.result, errors: payload.errors })
      })
      const payload = (message && typeof message === 'object') ? { id, ...message } : { id }
      iframeRef.current?.contentWindow?.postMessage(payload, '*')
    })
  }, [])

  // Set initial dark gray background when viewer loads
  useEffect(() => {
    const initializeViewer = async () => {
      if (loaded && iframeRef.current?.contentWindow && !viewerReadyRef.current) {
        try {
          // Wait for viewer to be ready before sending any messages
          await sendAndWait({ description: 'NotifyWhenLoaded' })
          viewerReadyRef.current = true
          
          // Now safe to set background
          sendMessage({
            description: 'SetBackground',
            result: { color: '#2a2a2a', colorDuration: 0 }, // Dark gray, instant
          })
        } catch (e) {
          setWebglError(true)
        }
      }
    }
    void initializeViewer()
  }, [loaded, sendMessage, sendAndWait])

  // Track if we're waiting for a component click response
  const pendingClickCheckRef = useRef<any>(null)
  const lastComponentClickTimeRef = useRef<number>(0)

  // Detect iframe interactions via mousedown events (which DO propagate from iframe)
  useEffect(() => {
    const handleMouseDown = () => {
      // Clear any existing timer
      if (pendingClickCheckRef.current) {
        clearTimeout(pendingClickCheckRef.current)
      }
      
      // Set a timeout to check if we got a component click notification
      pendingClickCheckRef.current = setTimeout(() => {
        const timeSinceLastComponentClick = Date.now() - lastComponentClickTimeRef.current
        
        // If we got a component click very recently (within last 200ms), don't clear
        if (timeSinceLastComponentClick < 200) {
          pendingClickCheckRef.current = null
          return
        }
        
        const assetName = currentAssetNameRef.current || 'PDBAsset'
        
        // Use void IIFE to handle async
        void (async () => {
          // Remove label
          try {
            await sendAndWait({ 
              description: 'RemoveAsset', 
              target: { name: activeLabelNameRef.current } 
            })
          } catch (e) {
            // Label may not exist
          }
          
          // Clear selection
          try {
            await sendAndWait({
              description: 'SetSelection',
              target: { name: assetName },
              result: {}
            })
          } catch (e) {
            // Selection clear failed
          }
          
          setLastClickInfo(null)
          setClickPos(null)
        })()
        
        pendingClickCheckRef.current = null
      }, 150) // Wait 150ms for component click notification
    }
    
    // Listen on document for mousedown (fires even when clicking in iframe)
    document.addEventListener('mousedown', handleMouseDown, true)
    
    return () => {
      document.removeEventListener('mousedown', handleMouseDown, true)
      if (pendingClickCheckRef.current) {
        clearTimeout(pendingClickCheckRef.current)
        pendingClickCheckRef.current = null
      }
    }
  }, []) // Empty deps - never re-run unless component unmounts

  // Subscribe to hover info from the viewer for a specific asset (persistent)
  const subscribeHoverInfo = useCallback((assetName: string) => {
    if (!assetName) return
    
    const id = softRandomUUID()
    persistentMapRef.current.set(id, (payload: any) => {
      const selectionData = payload?.result
      
      if (selectionData && !selectionData.cleared) {
        // Store hover info for display
        setHoverInfo({
          chain: selectionData.chain,
          residue: selectionData.residue,
          atom: selectionData.atom,
          timestamp: Date.now()
        })
        // Also track the residue ID for sequence viewer highlighting
        if (selectionData.residue !== undefined && selectionData.residue !== null) {
          setHoveredResidueId(selectionData.residue)
        }
      } else {
        // Clear hover info when nothing is hovered
        setHoverInfo(null)
        setHoveredResidueId(null)
      }
    })
    
    const msg = { 
      id, 
      description: 'SetHoverSelection',
      target: { name: assetName },
      result: true // Enable hover
    }
    iframeRef.current?.contentWindow?.postMessage(msg, '*')
  }, [])

  // Subscribe to click info from the viewer (persistent)
  const subscribeClickInfo = useCallback(() => {
    if (clickSubscribedRef.current) return
    const id = softRandomUUID()
    persistentMapRef.current.set(id, (payload: any) => {
      const aux = payload?.result?.auxData ?? payload?.auxData ?? null
      
      // Extract which component was clicked from the payload
      // The component object is at payload.result.component, and we want the baseAssetName (our naming) or name (viewer's naming)
      const component = payload?.result?.component || null
      const clickedComponentName = component?.baseAssetName || component?.name || currentAssetNameRef.current || 'PDBAsset'
      
      // Set this as the active molecule and update currentAssetNameRef
      if (clickedComponentName) {
        currentAssetNameRef.current = clickedComponentName
        setActiveMolecule(clickedComponentName)
        lastMoleculeClickTimeRef.current = Date.now() // Track click time for Delete key functionality
        
        // Blur the input so Delete key can work (not editing text)
        if (sidebarInputRef.current) {
          sidebarInputRef.current.blur()
        }
        if (welcomeInputRef.current) {
          welcomeInputRef.current.blur()
        }
        
      }
      
      // Clear the iframe click flag since we got a molecule click
      if (iframeClickDetectedRef.current) {
        iframeClickDetectedRef.current = false
      }
      
      // Record the time of this component click
      lastComponentClickTimeRef.current = Date.now()
      
      // Clear hover info on click
      setHoverInfo(null)
      
      // Clear any pending background click check - we got a response
      if (pendingClickCheckRef.current) {
        clearTimeout(pendingClickCheckRef.current)
        pendingClickCheckRef.current = null
      }
      
      // Check if aux has meaningful data (residue/atom info)
      const hasResidueInfo = aux && (aux.info?.residue || aux.residue)
      const hasAtomInfo = aux && (aux.atomId || aux.info?.atomId || aux.labelAtom || aux.info?.labelAtom)
      const hasMeaningfulData = hasResidueInfo || hasAtomInfo
      
      
      if (hasMeaningfulData) {
        if (backgroundClickTimerRef.current) { clearTimeout(backgroundClickTimerRef.current); backgroundClickTimerRef.current = null }
        setLastClickInfo(aux)
        
        // Log click information to console for debugging
        
        // Use last pointer position captured over the viewer container
        if (lastPointerRef.current) {
          setClickPos({ ...lastPointerRef.current })
        }
        // Update 3D label via landmarks: clear previous and add new
        ;(async () => {
          // Use the clicked component name from payload, not currentAssetNameRef which may be stale
          const assetName = clickedComponentName || currentAssetNameRef.current || 'PDBAsset'
          const labelName = activeLabelNameRef.current
          try { await sendAndWait({ description: 'RemoveAsset', target: { name: labelName } }) } catch {}
          try {
            // Derive label text from aux info
            const residue = aux?.info?.residue || aux?.residue
            const residueName = residue?.name || residue?.longCode
            // Try multiple ways to get residue number - the seqNumber is usually the correct one for selection
            const residueId = residue?.seqNumber || residue?.number || residue?.id
            const chain = aux?.info?.chain || aux?.chain
            const chainId = typeof chain === 'string' ? chain : (chain?.id || chain?.name)
            const atom = (aux.targetType === 'atom') ? aux?.labelAtom || aux?.info?.labelAtom || aux?.atomicSymbol || aux?.info?.atomicSymbol || aux?.element || aux?.symbol : undefined
            const atomId = aux?.info?.atomId || aux?.atomId
            
            // Log the full residue object to debug
            const labelText = String([residueName, atom].filter(Boolean).join(' • ') || 'Selection')
            const position = (aux?.info?.position || aux?.position)
            const fallbackPos = [0, 0, 0]
            const pos: [number, number, number] = Array.isArray(position) && position.length === 3 ? position as [number, number, number] : (fallbackPos as [number, number, number])
            
            // Add landmark label
            await sendAndWait({
              description: 'AddLandmarks',
              result: [
                {
                  title: labelText,
                  description: '',
                  parent: assetName,
                  position: pos,
                  shape: 'label',
                  name: labelName,
                  fontSize: 20,
                  markerScale: 7.5,
                  labelPadding: 10,
                  strokeWidth: 2,
                  strokeColor: [0.25, 0.6, 1, 1],
                  fillColor: [0.07, 0.09, 0.12, 0.92],
                  labelText,
                  depthBiasFactor: 1.0, // Push label closer to camera to avoid occlusion by surfaces
                  depthTest: false, // Disable depth testing so labels are always visible
                },
              ],
            })
            
            // Set selection to highlight the clicked region
            // IMPORTANT: Only send ONE level - most specific available
            // Per docs: "Chain is checked first, then residue, then atom. Only one of them should be defined"
            // We prefer RESIDUE over ATOM for better visibility (atoms are too small to see)
            
            let selectionParams: Record<string, any> | null = null
            // Prefer residue over atom for better visibility
            if (residueId !== undefined && residueId !== null) {
              selectionParams = { residue: residueId }
            } else if (atomId !== undefined && atomId !== null) {
              selectionParams = { atom: atomId }
            } else if (chainId) {
              selectionParams = { chain: chainId }
            }
            
            if (selectionParams) {
              const selectionResult = await sendAndWait({
                description: 'SetSelection',
                target: { name: assetName },
                result: selectionParams
              })
            } else {
            }
          } catch (e) {
          }
        })()
      } else {
        // No meaningful data — clicked empty space/background: remove the active label and clear selection after a short debounce
        if (backgroundClickTimerRef.current) clearTimeout(backgroundClickTimerRef.current)
        backgroundClickTimerRef.current = setTimeout(async () => {
          const assetName = currentAssetNameRef.current || 'PDBAsset'
          try { 
            // Remove the label
            await sendAndWait({ description: 'RemoveAsset', target: { name: activeLabelNameRef.current } }) 
          } catch (e) {
            // Silently ignore - label may not exist
          }
          
          // Clear selection - try with empty object (falsey value)
          try {
            const clearResult = await sendAndWait({
              description: 'SetSelection',
              target: { name: assetName },
              result: {}  // Empty object to clear
            })
          } catch (e) {
            // Silently ignore - selection may not be set
          }
          
          setLastClickInfo(null)
          setClickPos(null)
        }, 120)
      }
    })
    const msg = { id, description: 'NotifyWhenComponentClicked' }
    iframeRef.current?.contentWindow?.postMessage(msg, '*')
    clickSubscribedRef.current = true
  }, [sendAndWait])

  const waitForComponent = useCallback(async (name: string, attempts = 40, delayMs = 250): Promise<void> => {
    for (let i = 0; i < attempts; i++) {
      const { result, errors } = await sendAndWait({
        description: 'GetComponentProperties',
        target: { name },
      })
      if (!errors && result && result.properties) return
      await new Promise((r) => setTimeout(r, delayMs))
    }
    throw new Error(`Timeout waiting for component ${name}`)
  }, [sendAndWait])



  // Query the actual scene state from the viewer - source of truth!
  const querySceneState = useCallback(async () => {
    try {
      
      // Get all components in the scene
      const componentsRes = await sendAndWait({ description: 'GetComponents' })
      const components = componentsRes.result || { scenes: [], assets: [], landmarks: [] }
      
      
      // Build comprehensive scene state
      const state: any = {
        timestamp: new Date().toISOString(),
        assets: [],
        camera: null,
        selection: null,
      }

      // Get details for each asset
      for (const asset of components.assets || []) {
        try {
          const propsRes = await sendAndWait({ description: 'GetComponentProperties', target: { name: asset.name } })
          
          // Extract source URL and type from asset name for standalone viewer compatibility
          // Asset names follow pattern: PDB_1UBQ or CID_2244
          const assetName = asset.name || asset.baseAssetName || ''
          let source = 'unknown'
          let type = 'molecular-data'
          
          if (assetName.startsWith('PDB_')) {
            const pdbId = assetName.substring(4).toLowerCase()
            source = `https://files.rcsb.org/download/${pdbId}.cif`
          } else if (assetName.startsWith('CID_')) {
            const cid = assetName.substring(4)
            source = `https://pubchem.ncbi.nlm.nih.gov/rest/pug/compound/cid/${cid}/record/SDF/?record_type=3d`
          }
          
          const assetDetails: any = {
            name: asset.name,
            uuid: asset.uuid,
            baseAssetName: asset.baseAssetName,
            type: type,
            source: source,
            properties: propsRes.result?.properties,
            information: propsRes.result?.information,
          }
          
          // If it's a molecular asset AND the primary component (not a clone), get selection state
          // Primary assets: name === baseAssetName (e.g., CID_5793)
          // Viewer clones: name !== baseAssetName (e.g., CID_5793_0, CID_5793_1)
          const isPrimaryAsset = asset.name === asset.baseAssetName
          if (isPrimaryAsset) {
            try {
              const selectionRes = await sendAndWait({ description: 'GetSelection', target: { name: asset.name } })
              assetDetails.selection = selectionRes.result
            } catch (e) {
              // Selection might not be available for all asset types
            }
          }
          
          state.assets.push(assetDetails)
        } catch (e) {
        }
      }


      // Get camera position
      try {
        const cameraRes = await sendAndWait({ description: 'GetCameraPosition' })
        state.camera = cameraRes.result
      } catch (e) {
        // Camera position query failed
      }

      // Get background settings
      try {
        const backgroundRes = await sendAndWait({ description: 'GetBackground' })
        state.background = backgroundRes.result
      } catch (e) {
        // Background query failed
      }


      setSceneState(state)
      return state
    } catch (e) {
      return null
    }
  }, [sendAndWait])

  // Fuzzy matching helpers for mapping user text to available viewer options
  const stringDistance = useCallback((a: string, b: string): number => {
    const s = a.toLowerCase().trim(); const t = b.toLowerCase().trim()
    const m = s.length; const n = t.length
    const dp = Array.from({ length: m + 1 }, () => new Array(n + 1).fill(0))
    for (let i = 0; i <= m; i++) dp[i][0] = i
    for (let j = 0; j <= n; j++) dp[0][j] = j
    for (let i = 1; i <= m; i++) {
      for (let j = 1; j <= n; j++) {
        const cost = s[i - 1] === t[j - 1] ? 0 : 1
        dp[i][j] = Math.min(
          dp[i - 1][j] + 1,
          dp[i][j - 1] + 1,
          dp[i - 1][j - 1] + cost,
        )
      }
    }
    return dp[m][n]
  }, [])

  const normalizeAlias = useCallback((v: string): string => {
    const x = v.toLowerCase().trim()
    const map: Record<string,string> = {
      hydrophobe: 'hydrophobicity',
      hydrophobic: 'hydrophobicity',
      hydropathy: 'hydrophobicity',
      spectrum: 'spectrum',
      rainbow: 'spectrum',
    }
    return map[x] ?? x
  }, [])

  const coerceOption = useCallback((desired: any, options?: any[]): { matched: boolean; value: any } => {
    if (!options || !Array.isArray(options) || options.length === 0) return { matched: false, value: desired }
    if (typeof desired !== 'string') return { matched: true, value: desired }
    const lc = normalizeAlias(String(desired))
    const optsLC = options.map(o => String(o).trim().toLowerCase())
    // exact
    for (let i = 0; i < options.length; i++) {
      if (optsLC[i] === lc) return { matched: true, value: options[i] }
    }
    // substring
    for (let i = 0; i < options.length; i++) {
      if (optsLC[i].includes(lc)) return { matched: true, value: options[i] }
    }
    // fuzzy: pick closest within small threshold
    let bestIdx = -1; let best = Infinity
    for (let i = 0; i < options.length; i++) {
      const d = stringDistance(lc, optsLC[i])
      if (d < best) { best = d; bestIdx = i }
    }
    if (bestIdx >= 0 && best <= 2) {
      return { matched: true, value: options[bestIdx] }
    }
    return { matched: false, value: options[0] }
  }, [normalizeAlias, stringDistance])

  const normalizePropsAgainstInfo = useCallback((props: Record<string, any>, info: any): { normalized: Record<string, any>; failures: string[] } => {
    if (!info) return { normalized: props, failures: [] }
    const out: Record<string, any> = { ...props }
    const failures: string[] = []
    const withOptions: Array<{ key: string; optionsKey: string }> = [
      { key: 'colorScheme', optionsKey: 'colorSchemeOptions' },
      { key: 'surfaceColorScheme', optionsKey: 'surfaceColorSchemeOptions' },
      { key: 'style', optionsKey: 'styleOptions' },
    ]
    for (const { key, optionsKey } of withOptions) {
      if (out[key] !== undefined) {
        const opts = (info && Array.isArray((info as any)[optionsKey]) ? (info as any)[optionsKey] : undefined)
        if (opts && Array.isArray(opts) && opts.length > 0) {
          const { matched, value } = coerceOption(out[key], opts)
          out[key] = value
          if (!matched) failures.push(key)
        } else {
          // No options advertised by viewer; pass through without failing
          out[key] = out[key]
        }
      }
    }
    return { normalized: out, failures }
  }, [coerceOption])

  // Map a generic showLabels boolean to concrete label-related keys present in current properties
  const mapShowLabelsProps = useCallback((visible: boolean, currentPropsObj: Record<string, any>): Record<string, any> => {
    const lowerKeys = Object.keys(currentPropsObj || {}).map(k => [k, k.toLowerCase()] as const)
    const labelKeys = lowerKeys.filter(([k, lk]) => lk.includes('label'))
      .map(([k]) => k)
    const result: Record<string, any> = {}
    if (labelKeys.length) {
      for (const k of labelKeys) {
        if (typeof (currentPropsObj as any)[k] === 'boolean') {
          result[k] = visible
        }
      }
    }
    return result
  }, [])

  const buildOverlayFromInfo = useCallback((name: string, props: any, info: any, mode: 'auto'|'pdb'|'pubchem'): string => {
    const lines: string[] = []
    // Mode is always 'auto' now, so we don't display it
    lines.push('Scene: ROOT')
    if (props) {
      lines.push(`Component: ${name} (present)`) 
      if (info?.baseAssetName) lines.push(`BaseAsset: ${info.baseAssetName}`)
      if (info?.baseAssetUuid) lines.push(`UUID: ${info.baseAssetUuid}`)
      if (props.style) lines.push(`style: ${props.style}`)
      if (props.colorScheme) lines.push(`colorScheme: ${props.colorScheme}`)
      if (typeof props.thickness === 'number') lines.push(`thickness: ${props.thickness}`)
      if (typeof props.showSurface === 'boolean') lines.push(`showSurface: ${props.showSurface}`)
      if (typeof props.surfaceAlpha === 'number') lines.push(`surfaceAlpha: ${props.surfaceAlpha}`)
    } else {
      lines.push(`Component: ${name} (not present)`) 
    }
    lines.push('Layers: default')
    if (lastClickInfo) {
      try {
        const residueName = (lastClickInfo as any).info?.residue?.name
          || (lastClickInfo as any).residue?.name
          || (lastClickInfo as any).info?.residue?.longCode
          || (lastClickInfo as any).residue?.longCode
        const atomLabel = (lastClickInfo as any).labelAtom
          || (lastClickInfo as any).info?.labelAtom
          || (lastClickInfo as any).atomicSymbol
          || (lastClickInfo as any).info?.atomicSymbol
          || (lastClickInfo as any).element
          || (lastClickInfo as any).symbol
        if (residueName || atomLabel) {
          const parts: string[] = []
          if (residueName) parts.push(String(residueName))
          if (atomLabel) parts.push(String(atomLabel))
          lines.push(`Click: ${parts.join(' • ')}`)
        } else {
          const s = JSON.stringify(lastClickInfo)
          const t = s.length > 200 ? s.slice(0, 200) + '…' : s
          lines.push(`Click: ${t}`)
        }
      } catch {}
    }
    return lines.join('\n')
  }, [lastClickInfo])

  const refreshOverlay = useCallback(async () => {
    try {
      // Don't try to refresh if no molecules are loaded yet
      if (loadedAssets.length === 0) {
        if (showOverlay) setOverlayText(`Scene: ROOT\nNo molecules loaded\nLayers: default`)
        return
      }
      
      const assetName = currentAssetNameRef.current || 'PDBAsset'
      const gp = await sendAndWait({ description: 'GetComponentProperties', target: { name: assetName } })
      const props = gp.result?.properties
      const info = gp.result?.information
      setCurrentProps(props || {})
      setCurrentInfo(info || null)
      if (showOverlay) {
        setOverlayText(buildOverlayFromInfo(assetName, props, info, sourceMode))
      }
    } catch {
      if (showOverlay) {
        const assetName = currentAssetNameRef.current || 'PDBAsset'
        setOverlayText(`Scene: ROOT\nComponent: ${assetName} (not present)\nLayers: default`)
      }
    }
  }, [sendAndWait, showOverlay, buildOverlayFromInfo, sourceMode, loadedAssets])

  const buildSuggestionsFromInfo = useCallback((info: any): Array<{ label: string; props: Record<string, any> }> => {
    const out: Array<{ label: string; props: Record<string, any> }> = []
    const styles: string[] = Array.isArray(info?.styleOptions) ? info.styleOptions : []
    const colors: string[] = Array.isArray(info?.colorSchemeOptions) ? info.colorSchemeOptions : []
    for (const want of ['cartoon', 'ball-stick']) {
      const hit = styles.find((s: string) => String(s).toLowerCase() === want)
      if (hit) out.push({ label: `style: ${hit}`, props: { style: hit } })
    }
    out.push({ label: 'surface: on', props: { showSurface: true } })
    out.push({ label: 'surface: off', props: { showSurface: false } })
    for (const want of ['chain', 'element', 'rainbow']) {
      const hit = colors.find((c: string) => String(c).toLowerCase().includes(want))
      if (hit) out.push({ label: `color: ${hit}`, props: { colorScheme: hit } })
    }
    out.push({ label: 'thickness: thin', props: { thickness: 0.5 } })
    out.push({ label: 'thickness: thick', props: { thickness: 2 } })
    // Labels suggestions if properties include label toggles
    const propKeys = Object.keys((info as any) || {})
    const maybeHasLabels = propKeys.some(k => k.toLowerCase().includes('label'))
    if (maybeHasLabels) {
      out.push({ label: 'labels: on', props: { showLabels: true } })
      out.push({ label: 'labels: off', props: { showLabels: false } })
    }
    return out
  }, [])

  // Fetch protein structure (chains and residues) for sequence view (only for PDB-like assets)
  const loadStructure = useCallback(async () => {
    try {
      const assetName = currentAssetNameRef.current || 'PDBAsset'
      const { result, errors } = await sendAndWait({ description: 'GetProteinStructure', target: { name: assetName } })
      if (errors) throw new Error(String(errors))
      const chains = Array.isArray(result) ? result : (Array.isArray(result?.chains) ? result.chains : null)
      if (Array.isArray(chains) && chains.length) {
        setStructure(chains)
        setChainIdx(0)
      } else {
        setStructure(null)
      }
    } catch {
      setStructure(null)
    }
  }, [sendAndWait])

  const refreshSuggestions = useCallback(async () => {
    try {
      const assetName = currentAssetNameRef.current || 'PDBAsset'
      const gp = await sendAndWait({ description: 'GetComponentProperties', target: { name: assetName } })
      const info = gp.result?.information
      if (info) setSuggestions(buildSuggestionsFromInfo(info))
    } catch {
      setSuggestions([])
    }
  }, [sendAndWait, buildSuggestionsFromInfo])

  const applySuggestion = useCallback(async (props: Record<string, any>) => {
    try {
      const tname = currentAssetNameRef.current || 'PDBAsset'
      const gp = await sendAndWait({ description: 'GetComponentProperties', target: { name: tname } })
      const info = gp.result?.information
      const { normalized, failures } = normalizePropsAgainstInfo(props, info)
      if (failures.length) {
        // Offer choices for the failed keys
        const lines: string[] = []
        for (const key of failures) {
          const ok = String(key)
          const optionsKey = ok === 'surfaceColorScheme' ? 'surfaceColorSchemeOptions' : (ok === 'colorScheme' ? 'colorSchemeOptions' : (ok === 'style' ? 'styleOptions' : ''))
          const opts: any[] = (optionsKey && Array.isArray((info as any)[optionsKey])) ? (info as any)[optionsKey] : []
          if (opts.length) {
            lines.push(`${ok}: ${opts.join(', ')}`)
          }
        }
        if (lines.length) {
          setMessages(m => [...m, { role: 'assistant', content: `Which option did you mean?\n${lines.join('\n')}` }])
        }
        return
      }
      await sendAndWait({ description: 'SetComponentProperties', target: { name: tname }, result: normalized })
      setSuggestions([])
      await refreshOverlay()
      setMessages((m) => [...m, { role: 'assistant', content: `Applied: ${Object.keys(normalized).map(k => `${k}=${(normalized as any)[k]}`).join(', ')}` }])
    } catch (e: any) {
      setMessages((m) => [...m, { role: 'assistant', content: `Unable to apply: ${e?.message || e}` }])
    }
  }, [sendAndWait, normalizePropsAgainstInfo, refreshOverlay])

  // Helper to calculate scene-space center of a molecule
  const getSceneCenter = (props: any, info: any): [number, number, number] => {
    const position = props.position || [0, 0, 0]
    const scale = props.scale || [1, 1, 1]
    const boundingSphereCenter = info.boundingSphereCenter || [0, 0, 0]
    return [
      position[0] + boundingSphereCenter[0] * scale[0],
      position[1] + boundingSphereCenter[1] * scale[1],
      position[2] + boundingSphereCenter[2] * scale[2]
    ]
  }

  // Helper to calculate scene-space radius of a molecule
  const getSceneRadius = (props: any, info: any): number => {
    const scale = props.scale || [1, 1, 1]
    const boundingSphereRadius = info.boundingSphereRadius || 0
    return boundingSphereRadius * scale[0]
  }

  // Position and show multiple molecules side-by-side
  const positionAndShowMolecules = useCallback(async (assetNames: string[]) => {
    if (assetNames.length === 0) return
    
    const MARGIN = 0.1
    const assetDataList: Array<{ name: string, props: any, info: any }> = []
    
    // Gather properties for all assets
    for (const assetName of assetNames) {
      try {
        await waitForComponent(assetName, 40, 500)
        const propsRes = await sendAndWait({ description: 'GetComponentProperties', target: { name: assetName } })
        if (propsRes?.result?.properties && propsRes?.result?.information) {
          assetDataList.push({
            name: assetName,
            props: propsRes.result.properties,
            info: propsRes.result.information
          })
        }
      } catch (e) {
      }
    }
    const maxRadius = Math.max(...assetDataList.map(asset => asset.info.boundingSphereRadius))
    // Position each molecule
    for (let i = 0; i < assetDataList.length; i++) {
      const asset = assetDataList[i];
      let scale = asset.props.scale[0] || 1.;
      if (asset.info.boundingSphereRadius) {
        const effectiveScaleRadius = Math.min(asset.info.boundingSphereRadius*20, maxRadius)
        scale = NORMALIZED_MODEL_RADIUS/effectiveScaleRadius;
      }
      asset.props.scale = [scale, scale, scale];
      const radius = getSceneRadius(asset.props, asset.info)
      
      let offset = [0, 0, 0];

      // Alternate going right or left
      const sign = i%2?1:-1;
      const compFunc = i%2? Math.max : Math.min

      // Calculate offset based on previous molecules
      for (let j = 0; j < i; j++) {
        const otherAsset = assetDataList[j]
        const otherCenter = getSceneCenter(otherAsset.props, otherAsset.info)
        const otherRadius = getSceneRadius(otherAsset.props, otherAsset.info)
        offset[0] = compFunc(offset[0], otherCenter[0] + (otherRadius + radius + MARGIN)*sign);
      }
      
      // Calculate new position
      const newPosition = offset.map((v, idx) => v - asset.info.boundingSphereCenter[idx]*scale)
      asset.props.position = newPosition;
      
      // Set properties and make visible
      await sendAndWait({
        description: 'SetComponentProperties',
        target: { name: asset.name },
        result: {
          visible: true,
          position: newPosition,
          scale: [scale, scale, scale]
        }
      })
      
    }
  }, [sendAndWait, waitForComponent, getSceneCenter, getSceneRadius])

  const addAssets = useCallback(async (parsed: ParseResponse) => {
    // Optionally set background (global)
    if (parsed.globalViz?.background) {
      sendMessage({
        description: 'SetBackground',
        result: { color: parsed.globalViz.background },
      })
    }

    // Skip adding if backend returned no objects (update-only or clear)
    if (parsed.objects && parsed.objects.length > 0) {
      // Process all objects - support multiple molecules
      const objs = parsed.objects
      const MARGIN = 0.1
      
      // Get existing asset names and data (using molecule IDs for unique naming)
      const existingAssetData: Map<string, { props: any, info: any }> = new Map()
      
      // Gather existing asset properties for positioning calculations
      for (const asset of loadedAssets) {
        const existingName = asset.source === 'pdb' ? `PDB_${asset.id.toUpperCase()}` : `CID_${asset.id}`
        try {
          const propsRes = await sendAndWait({ description: 'GetComponentProperties', target: { name: existingName } })
          if (propsRes?.result?.properties && propsRes?.result?.information) {
            existingAssetData.set(existingName, {
              props: propsRes.result.properties,
              info: propsRes.result.information
            })
            }
          } catch {}
      }
      
      // Store new assets' data for positioning
      const newAssetData: Map<string, { props: any, info: any, source: string, id: string }> = new Map()
      const newAssetNames: string[] = []
      
      // STEP 1: Load all new assets with showOnLoad: false
      for (let i = 0; i < objs.length; i++) {
        const obj = objs[i]
        // Generate unique asset name based on molecule ID (avoids conflicts with viewer clones)
        const assetName = obj.source === 'pdb' ? `PDB_${obj.id.toUpperCase()}` : `CID_${obj.id}`
        
        // Skip if this molecule is already loaded
        const alreadyLoaded = loadedAssets.some(asset => 
          asset.source === obj.source && asset.id === obj.id
        )
        if (alreadyLoaded) {
          continue
        }
        
        newAssetNames.push(assetName)
        
        if (i === 0) {
          currentAssetNameRef.current = assetName
          setActiveMolecule(assetName)
        }
        if (obj.source === 'pdb') {
          const url = `https://files.rcsb.org/download/${obj.id}.cif`
          const result: Record<string, unknown> = {
            name: assetName,
            type: 'molecular-data',
            data: {
              url,
              type: 'cif',
              isBinary: false,
              colorScheme: 'chain',
              style: 'cartoon',
              thickness: 1,
              ...(obj.style && typeof obj.style === 'object' ? obj.style : {}),
            },
            scene: 'ROOT',
            scale: 0.06,  // Scale to 6% - balanced size for good visibility and camera range
            showOnLoad: false,
          }
          const addRes = await sendAndWait({ description: 'AddAssets', result: [result] })
          
          // Only track as loaded if there were no errors
          if (!addRes.errors || addRes.errors.length === 0) {
            try {
              await waitForComponent(assetName)
              // Verify component actually exists
              const verifyRes = await sendAndWait({ description: 'GetComponentProperties', target: { name: assetName } })
              if (verifyRes?.result?.properties) {
          // LOG ALL AVAILABLE PROPERTIES AND OPTIONS FOR PDB MOLECULES
          const info = verifyRes.result.information || {}
          for (const key of Object.keys(info)) {
            if (key.endsWith('Options')) {
            }
          }
          
          // Transition background to dark gradient
          sendMessage({
            description: 'SetBackground',
            result: { style: 'linear-gradient(to bottom, #000000, #0a1628, #1e1b4b)' }, // Black → dark blue → dark purple
          })
          subscribeClickInfo()
                subscribeHoverInfo(assetName) // Enable hover for this specific molecule
                
                // Store as successfully loaded - positioning will happen later
          await refreshSuggestions()
                newAssetData.set(assetName, { props: {}, info: {}, source: 'pdb', id: obj.id })
              }
            } catch (e) {
              // Remove the broken component
              try {
              await sendAndWait({ description: 'RemoveAsset', target: { name: assetName } })
          } catch {}
            }
          } else {
          }
        } else if (obj.source === 'pubchem') {
          const cid = String(obj.id)
          const tryAdd = async (recordType: '3d' | '2d') => {
            const url = `https://pubchem.ncbi.nlm.nih.gov/rest/pug/compound/cid/${cid}/sdf?record_type=${recordType}`
            const result: Record<string, unknown> = {
              name: assetName,
              type: 'molecular-data',
              data: {
                url,
                type: 'sdf',
                isBinary: false,
              },
              scene: 'ROOT',
              scale: 0.06,  // Scale to 6% - balanced size for good visibility and camera range
              showOnLoad: false,
            }
            const addRes = await sendAndWait({ description: 'AddAssets', result: [result] })
            return addRes
          }
          let added = false
          try {
            const res3d = await tryAdd('3d')
            if (res3d.errors && res3d.errors.length) {
              throw new Error('3d load failed')
            }
            await waitForComponent(assetName)
            // Verify component actually exists
            const verifyRes = await sendAndWait({ description: 'GetComponentProperties', target: { name: assetName } })
            if (verifyRes?.result?.properties) {
            // LOG ALL AVAILABLE PROPERTIES AND OPTIONS FOR PUBCHEM 3D MOLECULES
            const info = verifyRes.result.information || {}
            for (const key of Object.keys(info)) {
              if (key.endsWith('Options')) {
              }
            }
            
            // Transition background to dark gradient
            sendMessage({
              description: 'SetBackground',
              result: { style: 'linear-gradient(to bottom, #000000, #0a1628, #1e1b4b)' }, // Black → dark blue → dark purple
            })
            subscribeClickInfo()
              subscribeHoverInfo(assetName) // Enable hover for this specific molecule
              
              // Store as successfully loaded - positioning will happen later
            await refreshSuggestions()
              newAssetData.set(assetName, { props: {}, info: {}, source: 'pubchem', id: obj.id })
            added = true
            }
          } catch {
            // Try 2D fallback if 3D fails
            try {
              const res2d = await tryAdd('2d')
              if (!(res2d.errors && res2d.errors.length)) {
                await waitForComponent(assetName)
                // Verify component actually exists
                const verifyRes = await sendAndWait({ description: 'GetComponentProperties', target: { name: assetName } })
                if (verifyRes?.result?.properties) {
                // LOG ALL AVAILABLE PROPERTIES AND OPTIONS FOR PUBCHEM 2D MOLECULES
                const info = verifyRes.result.information || {}
                for (const key of Object.keys(info)) {
                  if (key.endsWith('Options')) {
                  }
                }
                
                // Transition background to black
                sendMessage({
                  description: 'SetBackground',
                  result: { color: '#000000', colorDuration: 1 }, // Black with 1s transition
                })
                subscribeClickInfo()
                  subscribeHoverInfo(assetName) // Enable hover for this specific molecule
                  
                  // Store as successfully loaded - positioning will happen later
                await refreshSuggestions()
                  newAssetData.set(assetName, { props: {}, info: {}, source: 'pubchem', id: obj.id })
                added = true
                }
              }
            } catch {}
          }
          if (!added) {
          }
        }
        // PubChem or others could be added similarly here
      }
      
      // STEP 2: Position and show all newly loaded molecules
      if (newAssetData.size > 0) {
        const loadedNames = Array.from(newAssetData.keys())
        
        try {
          // Get ALL asset names (existing + new) for positioning
          const existingNames = Array.from(existingAssetData.keys())
          const allAssetNames = [...existingNames, ...loadedNames]
          
          // Position and show all molecules
          await positionAndShowMolecules(allAssetNames)
                    
          // Debug: Query what's actually in the scene
          try {
            const componentsRes = await sendAndWait({ description: 'GetComponents' })
            const components = componentsRes?.result?.components || []
          } catch (e) {
          }
          
          // Append successfully loaded assets to state
          const newLoadedAssets = Array.from(newAssetData.values()).map(data => ({
            source: data.source as 'pdb' | 'pubchem',
            id: data.id
          }))
          setLoadedAssets(prev => [...prev, ...newLoadedAssets])
          
        } catch (e) {
        }
      }
    }

    // Apply property updates from backend, if any
    if (parsed.updates && parsed.updates.length) {
      for (const upd of parsed.updates) {
        const tname = (upd as any).targetName || currentAssetNameRef.current || 'PDBAsset'
        const props = (upd as any).props || {}
        // read current info for valid option sets
        const gp = await sendAndWait({ description: 'GetComponentProperties', target: { name: tname } })
        const info = gp.result?.information
        const currentPropsObj = gp.result?.properties || {}
        let toApply: Record<string, any> = { ...props }
        // Translate generic showLabels to concrete keys if present
        if (typeof (toApply as any).showLabels === 'boolean') {
          const labelMapped = mapShowLabelsProps((toApply as any).showLabels, currentPropsObj)
          delete (toApply as any).showLabels
          // Only apply if there are actual label keys available
          if (Object.keys(labelMapped).length) {
            toApply = { ...toApply, ...labelMapped }
          }
        }
        const { normalized, failures } = normalizePropsAgainstInfo(toApply, info)
        if (failures.length) {
          const lines: string[] = []
          for (const key of failures) {
            const ok = String(key)
            const optionsKey = ok === 'surfaceColorScheme' ? 'surfaceColorSchemeOptions' : (ok === 'colorScheme' ? 'colorSchemeOptions' : (ok === 'style' ? 'styleOptions' : ''))
            const opts: any[] = (optionsKey && Array.isArray((info as any)[optionsKey])) ? (info as any)[optionsKey] : []
            if (opts.length) lines.push(`${ok}: ${opts.join(', ')}`)
          }
          if (lines.length) setMessages(m => [...m, { role: 'assistant', content: `Which option did you mean?\n${lines.join('\n')}` }])
          // Also surface quick suggestion chips for the first failed key
          const firstKey = failures[0]
          const firstOptionsKey = firstKey === 'surfaceColorScheme' ? 'surfaceColorSchemeOptions' : (firstKey === 'colorScheme' ? 'colorSchemeOptions' : (firstKey === 'style' ? 'styleOptions' : ''))
          const opts: any[] = (firstOptionsKey && Array.isArray((info as any)[firstOptionsKey])) ? (info as any)[firstOptionsKey] : []
          if (opts.length) setSuggestions(opts.map(opt => ({ label: `${firstKey}: ${String(opt)}`, props: { [firstKey]: opt } })))
          return
        }
        await sendAndWait({ description: 'SetComponentProperties', target: { name: tname }, result: normalized })
      }
    }

    // Handle actions like clear
    if (parsed.actions && parsed.actions.length) {
      for (const act of parsed.actions) {
        if ((act as any).type === 'clear') {
          // Remove ALL loaded molecules
          for (const asset of loadedAssets) {
            const assetName = asset.source === 'pdb' ? `PDB_${asset.id.toUpperCase()}` : `CID_${asset.id}`
            try {
              await sendAndWait({ description: 'RemoveAsset', target: { name: assetName } })
            } catch (err) {
            }
          }
          // Clear loaded assets tracking
          setLoadedAssets([])
          setHoverInfo(null)
          // Transition background back to dark gray
          sendMessage({
            description: 'SetBackground',
            result: { color: '#2a2a2a', colorDuration: 1 }, // Dark gray with 1s transition
          })
        }
      }
    }
    await refreshOverlay()
  }, [sendMessage, sendAndWait, waitForComponent, positionAndShowMolecules, normalizePropsAgainstInfo, loadedAssets, subscribeClickInfo, subscribeHoverInfo, refreshSuggestions])

  const handleClearAll = useCallback(async () => {
    try {
      await sendAndWait({ description: 'RemoveAsset', target: { name: currentAssetNameRef.current || 'PDBAsset' } })
      setMessages((m) => [...m, { role: 'assistant', content: 'Cleared all molecules from the scene.' }])
      await refreshOverlay()
    } catch (e: any) {
      setMessages((m) => [...m, { role: 'assistant', content: `Unable to clear: ${e?.message || e}` }])
    }
  }, [sendAndWait, refreshOverlay])

  const handleSubmit = useCallback(async () => {
    if (!input.trim()) return
    // Mark that user has interacted - open the side panel for first-time users
    const wasFirstTimeUser = firstTimeUser
    if (wasFirstTimeUser) {
      setShowPromptPanel(true)
    }
    setFirstTimeUser(false)
    // Intercept confirmation via prompt when awaiting a clear
    if (awaitingClearConfirm) {
      const t = input.trim().toLowerCase()
      const yes = ["yes","y","ok","okay","confirm","do it","sure","please do","go ahead"]
      const no = ["no","n","cancel","stop","never mind","nevermind"]
      if (yes.some(w => t === w || t.includes(w))) {
        try {
          await sendAndWait({ description: 'RemoveAsset', target: { name: currentAssetNameRef.current || 'PDBAsset' } })
        } catch {}
        setAwaitingClearConfirm(false)
        setMessages((m) => [...m, { role: 'user', content: input }, { role: 'assistant', content: 'Cleared all molecules from the scene.' }])
        setInput('')
        await refreshOverlay()
        return
      }
      if (no.some(w => t === w || t.includes(w))) {
        setAwaitingClearConfirm(false)
        setMessages((m) => [...m, { role: 'user', content: input }, { role: 'assistant', content: 'Okay, I will not clear the scene.' }])
        setInput('')
        return
      }
      // Ask again if unclear
      setMessages((m) => [...m, { role: 'user', content: input }, { role: 'assistant', content: 'Please reply yes or no to confirm clearing everything.' }])
      setInput('')
      return
    }
    // Ensure the viewer signals it is fully loaded
    if (!viewerReadyRef.current) {
      await sendAndWait({ description: 'NotifyWhenLoaded' })
      sendMessage({ description: 'SetLandmarkCameraAction', result: "lookAt"} ) // Disable moving the camera when clicking on landmarks
      viewerReadyRef.current = true
    }
    // Determine if there is an existing asset by checking the actual viewer
    let hasAsset = false
    try {
      const gp = await sendAndWait({ description: 'GetComponentProperties', target: { name: currentAssetNameRef.current || 'PDBAsset' } })
      hasAsset = !!(gp.result && gp.result.properties)
      
      // Note: Don't automatically clear loadedAssets here - multi-molecule support means
      // hasAsset check is unreliable for the primary asset only
    } catch {}
    const nextHistory: ChatMessage[] = [...messages, { role: 'user', content: input }]
    setMessages(nextHistory)
    
    // Add to command history (avoid duplicates of last command)
    setCommandHistory(prev => {
      if (prev.length === 0 || prev[prev.length - 1] !== input) {
        return [...prev, input]
      }
      return prev
    })
    setHistoryIndex(-1) // Reset history navigation
    
    setInput('')
    setWorking(true)
    
    try {
      // Check if PyMOL mode is enabled
      if (pymolMode) {
        const assetName = currentAssetNameRef.current || 'PDBAsset'
        const pymolResult = parsePyMolCommands(input, assetName)
        
        if (pymolResult.error) {
          setMessages((m) => [...m, { role: 'assistant', content: `PyMOL Error: ${pymolResult.error}` }])
          return
        }
        
        // Execute the messages sequentially
        for (const msg of pymolResult.messages) {
          
          if (msg.description === 'AddAssets') {
            // Handle AddAssets through the addAssets flow
            const mockParseResponse: ParseResponse = {
              objects: msg.result.map((asset: any) => ({
                source: asset.data.url.includes('pubchem') ? 'pubchem' : 'pdb',
                id: asset.data.url.match(/([A-Z0-9]{4})\.cif/i)?.[1] || asset.data.url.match(/\/cid\/(\d+)\//i)?.[1] || 'unknown',
                targetName: asset.name || 'PDBAsset',
                position: asset.position,
                scale: asset.scale,
                style: asset.data
              })),
              updates: [],
              notes: pymolResult.assistantResponse || 'Loading structure'
            }
            await addAssets(mockParseResponse)
          } else {
            // Execute other messages directly
            await sendAndWait(msg)
          }
        }
        
        // Add assistant response
        const response = pymolResult.assistantResponse || 'PyMOL command executed'
        setMessages((m) => [...m, { role: 'assistant', content: response }])
        return
      }
      
      // Query actual scene state from viewer (source of truth!)
      const currentSceneState = await querySceneState()
      // Only count PRIMARY molecular assets (not viewer-generated clones like CID_5793_0, CID_5793_1)
      const molecularAssets = currentSceneState?.assets?.filter((a: any) => {
        const name = a.name || ''
        const baseAssetName = a.baseAssetName || ''
        
        // Our molecules: CID_5793, PDB_1SMD (baseAssetName matches name exactly)
        // Viewer clones: CID_5793_0, CID_5793_1 (baseAssetName !== name)
        const isPrimaryAsset = name === baseAssetName
        return isPrimaryAsset
      }) || []
      const hasActualAsset = molecularAssets.length > 0
      
      lastSentSourceModeRef.current = sourceMode
      
      // Build detailed molecule list for LLM context FROM ACTUAL SCENE STATE (source of truth!)
      // This ensures we always send accurate info, even if loadedAssets state is stale
      const moleculeDetails = molecularAssets.map((asset: any) => {
        const assetName = asset.name || asset.baseAssetName || ''
        // Parse asset name to extract source and ID
        // Asset names follow pattern: PDB_1UBQ or CID_2244
        const isPdb = assetName.startsWith('PDB_')
        const isPubchem = assetName.startsWith('CID_')
        
        if (isPdb) {
          const id = assetName.substring(4).toUpperCase() // Remove "PDB_" prefix
          return { source: 'pdb', id, name: `PDB ${id}` }
        } else if (isPubchem) {
          const id = assetName.substring(4) // Remove "CID_" prefix
          return { source: 'pubchem', id, name: `PubChem CID ${id}` }
        } else {
          // Fallback for unknown format
          return { source: 'unknown', id: assetName, name: assetName }
        }
      })
      
      // Log backend request
      
      const endpoint = `${BACKEND_URL}/parse`
      
      let res: Response
      try {
        res = await fetch(endpoint, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ 
            prompt: input, 
            history: nextHistory, 
            hasAsset: hasActualAsset, 
            sourceMode,
            currentAssets: loadedAssets, // Keep for backwards compat
            moleculeDetails, // NEW: Detailed molecule list with names
            sceneState: currentSceneState // Full scene state
          }),
        })
      } catch (fetchError: any) {
        throw fetchError
      }
      
      let data: ParseResponse
      try {
        data = await res.json()
      } catch (jsonError: any) {
        const textResponse = await res.text()
        throw new Error(`Server returned ${res.status}: ${textResponse.substring(0, 100)}`)
      }
      
      // Log backend response
      const responseInfo = {
        objects: (data.objects || []).map(o => ({ source: o.source, id: o.id })),
        updatesCount: data.updates?.length || 0,
        actions: data.actions,
        hasClarify: !!data.clarify,
        hasAssistant: !!data.assistant,
      }
      if (data.assistant) {
      }
      // Prompt-driven confirmation: ask in chat and set awaiting flag
      if ((data as any).requiresConfirmation) {
        setAwaitingClearConfirm(true)
        setMessages((m) => [...m, { role: 'assistant', content: 'Are you sure you want to clear everything? Reply yes to confirm.' }])
        return
      }
      await addAssets(data)
      if (data.clarify) {
        setMessages((m) => [...m, { role: 'assistant', content: data.clarify as string }])
      } else if (data.assistant) {
        setMessages((m) => [...m, { role: 'assistant', content: data.assistant as string }])
      } else {
        setMessages((m) => [...m, { role: 'assistant', content: data.notes ?? 'Updated visualization.' }])
      }
    } catch (e: any) {
      setMessages((m) => [...m, { role: 'assistant', content: `Error: ${e?.message || e}` }])
    } finally {
      setWorking(false)
    }
  }, [input, messages, addAssets])

  // Intro animation effect
  useEffect(() => {
    const timer = setTimeout(() => {
      setIntroComplete(true)
      // Show settings flash after intro completes and there are no assets yet
      if (loadedAssets.length === 0) {
        setTimeout(() => {
          setShowSettingsFlash(true)
          setTimeout(() => setShowSettingsFlash(false), 3000)
        }, 2000)
      }
    }, 1500)
    return () => clearTimeout(timer)
  }, [])

  // Reload protein structure when active molecule changes
  useEffect(() => {
    if (activeMolecule && showSequence) {
      loadStructure()
    }
  }, [activeMolecule, showSequence, loadStructure])

  // Keyboard handler for Delete/Backspace to delete active molecule
  useEffect(() => {
    const handleDeleteKey = (e: KeyboardEvent) => {
      // Only trigger for Delete or Backspace keys
      if (e.key !== 'Delete' && e.key !== 'Backspace') return
      
      // Don't trigger if user is typing in an input/textarea
      const target = e.target as HTMLElement
      if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA') return
      
      // Don't trigger if modals are open
      if (showReportModal || showDeleteConfirm || showPymolHelp) return
      
      // Only trigger if there's an active molecule
      if (!activeMolecule) return
      
      // Check if we're within the interaction window (user recently clicked molecule)
      const timeSinceClick = Date.now() - lastMoleculeClickTimeRef.current
      if (timeSinceClick > DELETE_INTERACTION_WINDOW) {
        return
      }
      
      // Prevent default backspace navigation behavior
      e.preventDefault()
      
      // Show delete confirmation
      setMoleculeToDelete(activeMolecule)
      setShowDeleteConfirm(true)
    }
    
    window.addEventListener('keydown', handleDeleteKey)
    return () => window.removeEventListener('keydown', handleDeleteKey)
  }, [activeMolecule, showReportModal, showDeleteConfirm, showPymolHelp, DELETE_INTERACTION_WINDOW])

  // Flash settings button whenever a new molecule is loaded (only when count increases)
  useEffect(() => {
    const currentCount = loadedAssets.length
    const prevCount = prevAssetCountRef.current
    
    // Only flash if we added molecules (count increased), not if we removed them
    if (currentCount > prevCount && currentCount > 0) {
      setShowSettingsFlash(true)
      const timer = setTimeout(() => setShowSettingsFlash(false), 3000)
      prevAssetCountRef.current = currentCount
      return () => clearTimeout(timer)
    }
    
    // Update the ref for next comparison
    prevAssetCountRef.current = currentCount
  }, [loadedAssets])

  // Auto-update sequence viewer when new molecule is loaded
  useEffect(() => {
    if (showSequence && loadedAssets.length > 0) {
      loadStructure()
    }
  }, [loadedAssets, showSequence, loadStructure])

  return (
    <>
      {/* Inject CSS animations */}
      <style>{`
        /* AI Panel Responsive Styles */
        @media (max-width: 768px) {
          .ai-panel-responsive {
            width: calc(100vw - 60px) !important;
            max-width: 420px !important;
          }
          .collapse-tab-responsive {
            right: -24px !important;
            width: 44px !important;
            height: 70px !important;
          }
        }
        
        @media (max-width: 480px) {
          .ai-panel-responsive {
            width: calc(100vw - 50px) !important;
          }
          .collapse-tab-responsive {
            right: -22px !important;
            width: 42px !important;
            height: 60px !important;
          }
        }
        
        @keyframes smoothFade {
          0% {
            opacity: 1;
            transform: scale(1);
          }
          65% {
            opacity: 1;
            transform: scale(1);
          }
          100% {
            opacity: 0;
            transform: scale(1.05);
          }
        }

        @keyframes iconSpinIn {
          0% {
            opacity: 0;
            transform: translateY(-50%) rotate(0deg) scale(0.3);
            box-shadow: none;
          }
          60% {
            opacity: 1;
            transform: translateY(-50%) rotate(1080deg) scale(1.1);
            box-shadow: none;
          }
          80% {
            transform: translateY(-50%) rotate(1080deg) scale(0.95);
            box-shadow: none;
          }
          95% {
            opacity: 1;
            transform: translateY(-50%) rotate(1080deg) scale(1);
            box-shadow: none;
          }
          100% {
            opacity: 1;
            transform: translateY(-50%) rotate(1080deg) scale(1);
            box-shadow: 0 8px 24px rgba(99, 102, 241, 0.4);
          }
        }

        @keyframes borderGlow {
          0%, 100% {
            border-color: rgba(99, 102, 241, 0.8);
            box-shadow: 0 0 30px rgba(99, 102, 241, 0.6), 
                        0 0 60px rgba(139, 92, 246, 0.4),
                        inset 0 0 20px rgba(99, 102, 241, 0.1);
          }
          50% {
            border-color: rgba(139, 92, 246, 1);
            box-shadow: 0 0 40px rgba(139, 92, 246, 0.8), 
                        0 0 80px rgba(99, 102, 241, 0.5),
                        inset 0 0 30px rgba(139, 92, 246, 0.2);
          }
        }

        @keyframes fadeInUp {
          0% {
            opacity: 0;
            transform: translate(-50%, -45%);
          }
          100% {
            opacity: 1;
            transform: translate(-50%, -50%);
          }
        }

        @keyframes pulse {
          0%, 100% {
            transform: scale(1);
          }
          50% {
            transform: scale(1.05);
          }
        }

        @keyframes settingsFlash {
          0%, 100% {
            box-shadow: 0 4px 16px rgba(0,0,0,0.3);
            transform: translateY(0) scale(1);
          }
          25% {
            box-shadow: 0 0 0 8px rgba(99, 102, 241, 0.4),
                        0 0 0 16px rgba(99, 102, 241, 0.2),
                        0 0 40px rgba(99, 102, 241, 0.6),
                        0 4px 16px rgba(0,0,0,0.3);
            transform: translateY(0) scale(1.15);
          }
          50% {
            box-shadow: 0 0 0 12px rgba(139, 92, 246, 0.3),
                        0 0 0 24px rgba(139, 92, 246, 0.1),
                        0 0 50px rgba(139, 92, 246, 0.5),
                        0 4px 16px rgba(0,0,0,0.3);
            transform: translateY(0) scale(1.1);
          }
          75% {
            box-shadow: 0 0 0 8px rgba(99, 102, 241, 0.4),
                        0 0 0 16px rgba(99, 102, 241, 0.2),
                        0 0 40px rgba(99, 102, 241, 0.6),
                        0 4px 16px rgba(0,0,0,0.3);
            transform: translateY(0) scale(1.15);
          }
        }

        @keyframes settingsGlow {
          0%, 100% {
            filter: brightness(1);
          }
          50% {
            filter: brightness(1.3) drop-shadow(0 0 12px rgba(99, 102, 241, 0.8));
          }
        }

        @keyframes molViewerDollyZoom {
          0%, 100% {
            transform: scale(1);
          }
          50% {
            transform: scale(1.15);
          }
        }

        @keyframes thinkingLoader {
          0%,25% {
            background-size: 8px 0,8px 4px,8px 4px,8px 0,8px 4px,8px 4px,8px 0,8px 4px,8px 4px;
            background-position: 0 50%,0 calc(50% - 2px),0 calc(50% + 2px),50% 50%,50% calc(50% - 2px),50% calc(50% + 2px),100% 50%,100% calc(50% - 2px),100% calc(50% + 2px);
          }
          50% {
            background-size: 8px 100%,8px 4px,8px 4px,8px 0,8px 4px,8px 4px,8px 0,8px 4px,8px 4px;
            background-position: 0 50%,0 calc(0% - 2px),0 calc(100% + 2px),50% 50%,50% calc(50% - 2px),50% calc(50% + 2px),100% 50%,100% calc(50% - 2px),100% calc(50% + 2px);
          }
          75% {
            background-size: 8px 100%,8px 4px,8px 4px,8px 100%,8px 4px,8px 4px,8px 0,8px 4px,8px 4px;
            background-position: 0 50%,0 calc(0% - 2px),0 calc(100% + 2px),50% 50%,50% calc(0% - 2px),50% calc(100% + 2px),100% 50%,100% calc(50% - 2px),100% calc(50% + 2px);
          }
          95%,100% {
            background-size: 8px 100%,8px 4px, 8px 4px,8px 100%,8px 4px,8px 4px,8px 100%,8px 4px,8px 4px;
            background-position: 0 50%,0 calc(0% - 2px),0 calc(100% + 2px),50% 50%,50% calc(0% - 2px),50% calc(100% + 2px),100% 50%,100% calc(0% - 2px),100% calc(100% + 2px);
          }
        }

        .thinking-loader {
          display: block;
          --c: linear-gradient(#fff 0 0);
          --r1: radial-gradient(farthest-side at bottom,#fff 93%,#0000);
          --r2: radial-gradient(farthest-side at top,#fff 93%,#0000);
          background: 
            var(--c), var(--r1), var(--r2),
            var(--c), var(--r1), var(--r2),
            var(--c), var(--r1), var(--r2);
          background-repeat: no-repeat;
          animation: thinkingLoader 1s infinite alternate;
        }
      `}</style>

      {/* Smooth gradient intro overlay */}
      <div 
        style={{
          position: 'fixed',
          inset: 0,
          background: 'linear-gradient(135deg, #4f46e5, #7c3aed, #2563eb)',
          animation: 'smoothFade 1.5s ease-in-out forwards',
          zIndex: 9999,
          pointerEvents: 'none'
        }}
      />

    <div style={{ display: 'flex', height: '100%', width: '100%', minHeight: 0, position: 'relative', overflow: 'hidden' }}>
        {/* AI Panel - slides in from left */}
        <aside
          className="ai-panel-responsive"
          style={{ 
            position: 'absolute',
            left: showPromptPanel ? 0 : 'calc(-100vw)',
            top: '70px',  // Start below header (header is 70px tall)
            bottom: 0,
            width: 420,
            background: 'rgba(15, 16, 20, 0.85)',
            backdropFilter: 'blur(12px)',
            WebkitBackdropFilter: 'blur(12px)',
            borderRight: '1px solid rgba(35, 36, 42, 0.6)',
            display: 'flex',
            flexDirection: 'column',
            transition: 'left 300ms cubic-bezier(0.4, 0, 0.2, 1)',
            zIndex: 100,
            boxShadow: showPromptPanel ? '4px 0 24px rgba(0,0,0,0.5)' : 'none',
            overflow: 'hidden'
          }}
        >
          {/* Header with controls */}
          <div style={{ 
            padding: '12px 16px',
            borderBottom: '1px solid rgba(35, 36, 42, 0.6)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 8,
            background: 'rgba(10, 11, 14, 0.4)'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M13 2L3 14h8l-1 8 10-12h-8l1-8z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
              </svg>
              <span style={{ fontWeight: 600 }}>AI Assistant</span>
        </div>
            <button
              onClick={() => setShowHistory(!showHistory)}
              title={showHistory ? "Hide history" : "Show history"}
              style={{
                padding: '6px 10px',
                borderRadius: 6,
                border: '1px solid rgba(47, 48, 55, 0.6)',
                background: showHistory ? 'rgba(29, 78, 216, 0.8)' : 'rgba(19, 20, 26, 0.6)',
                color: '#e5e7eb',
                cursor: 'pointer',
                fontSize: 12
              }}
            >
              {showHistory ? 'Hide History' : 'Show History'}
            </button>
          </div>
          
          {/* Collapse button - on right edge (only visible when panel is open) */}
          {showPromptPanel && (
            <button
              className="collapse-tab-responsive"
              onClick={() => setShowPromptPanel(false)}
              title="Collapse panel"
              style={{
                position: 'absolute',
                right: -20,
                top: '50%',
                transform: 'translateY(-50%)',
                width: 40,
                height: 80,
                borderRadius: '0 12px 12px 0',
                border: '1px solid rgba(99, 102, 241, 0.3)',
                borderLeft: 'none',
                background: 'rgba(99, 102, 241, 0.15)',
                backdropFilter: 'blur(8px)',
                WebkitBackdropFilter: 'blur(8px)',
                color: '#a5b4fc',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                transition: 'all 200ms cubic-bezier(0.4, 0, 0.2, 1)',
                zIndex: 101,
                padding: 0
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = 'rgba(99, 102, 241, 0.25)'
                e.currentTarget.style.borderColor = 'rgba(99, 102, 241, 0.5)'
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'rgba(99, 102, 241, 0.15)'
                e.currentTarget.style.borderColor = 'rgba(99, 102, 241, 0.3)'
              }}
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M15 18l-6-6 6-6" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </button>
          )}

          {/* Chat History */}
          {showHistory && (
            <div ref={messagesContainerRef} style={{ padding: 16, overflow: 'auto', flex: 1, minHeight: 0 }}>
            {messages.length === 0 && (
                <div style={{ 
                  opacity: 0.5, 
                  textAlign: 'center',
                  padding: '40px 20px',
                  fontSize: 14
                }}>
                  Try: "Show ubiquitin" or "Display caffeine molecule"
                </div>
            )}
            {messages.map((m, i) => (
                <div key={i} style={{ 
                  marginBottom: 16,
                  padding: 12,
                  borderRadius: 8,
                  background: m.role === 'user' ? 'rgba(19, 20, 26, 0.6)' : 'rgba(26, 27, 35, 0.6)',
                  border: '1px solid rgba(35, 36, 42, 0.5)',
                  backdropFilter: 'blur(4px)',
                  WebkitBackdropFilter: 'blur(4px)'
                }}>
                  <div style={{ 
                    fontSize: 11,
                    color: '#9ca3af',
                    marginBottom: 6,
                    textTransform: 'uppercase',
                    fontWeight: 600,
                    letterSpacing: '0.5px',
                    opacity: 0.7
                  }}>
                    {m.role === 'user' ? 'You' : 'Snorkle Assistant'}
                  </div>
                  <div style={{ fontSize: 14, lineHeight: 1.5, color: '#e5e7eb' }}>{m.content}</div>
              </div>
            ))}
          </div>
          )}

          {/* Input Area */}
          <div style={{ 
            borderTop: '1px solid rgba(35, 36, 42, 0.6)',
            padding: 16,
            background: 'rgba(10, 11, 14, 0.4)',
            backdropFilter: 'blur(8px)',
            WebkitBackdropFilter: 'blur(8px)'
          }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {/* Active Molecule Indicator */}
            {activeMolecule && loadedAssets.length > 1 && (
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: 6,
                fontSize: 11,
                color: '#9ca3af',
                paddingBottom: 4,
                borderBottom: '1px solid rgba(55, 65, 81, 0.3)'
              }}>
                <span style={{ opacity: 0.7 }}>Active:</span>
                <span style={{ 
                  color: '#6366f1', 
                  fontWeight: 600,
                  fontSize: 12
                }}>{activeMolecule}</span>
                <span style={{ 
                  fontSize: 10, 
                  opacity: 0.6,
                  fontStyle: 'italic'
                }}>(click any molecule to switch)</span>
              </div>
            )}
            {/* PyMOL Mode Toggle */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, paddingBottom: 4, justifyContent: 'space-between' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <label style={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: 8, 
                  cursor: 'pointer',
                  fontSize: 12,
                  color: pymolMode ? '#6366f1' : '#9ca3af',
                  fontWeight: pymolMode ? 600 : 400,
                  transition: 'all 200ms ease'
                }}>
              <input
                    type="checkbox"
                    checked={pymolMode}
                    onChange={(e) => setPymolMode(e.target.checked)}
                    style={{ 
                      width: 16, 
                      height: 16, 
                      accentColor: '#6366f1',
                      cursor: 'pointer'
                    }}
                  />
                  <span>PyMOL Mode (Lite)</span>
                </label>
                <div style={{ position: 'relative' }}>
                  <button
                    onClick={() => setShowPymolHelp(true)}
                    style={{
                      background: 'none',
                      border: 'none',
                      cursor: 'pointer',
                      padding: 2,
                      color: hoveredTooltip === 'pymol-help' ? '#6366f1' : '#6b7280',
                      fontSize: 14,
                      display: 'flex',
                      alignItems: 'center',
                      transition: 'color 200ms'
                    }}
                    onMouseEnter={() => setHoveredTooltip('pymol-help')}
                    onMouseLeave={() => setHoveredTooltip(null)}
                  >
                    ⓘ
                  </button>
                  {hoveredTooltip === 'pymol-help' && (
                    <div style={{
                      position: 'absolute',
                      bottom: '100%',
                      left: '50%',
                      transform: 'translateX(-50%)',
                      marginBottom: 6,
                      padding: '6px 10px',
                      background: 'rgba(17, 24, 39, 0.95)',
                      border: '1px solid rgba(99, 102, 241, 0.3)',
                      borderRadius: 6,
                      color: '#e5e9f5',
                      fontSize: 12,
                      whiteSpace: 'nowrap',
                      zIndex: 1000,
                      pointerEvents: 'none',
                      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.4)'
                    }}>
                      PyMOL Commands Help
                      <div style={{
                        position: 'absolute',
                        top: '100%',
                        left: '50%',
                        transform: 'translateX(-50%)',
                        width: 0,
                        height: 0,
                        borderLeft: '5px solid transparent',
                        borderRight: '5px solid transparent',
                        borderTop: '5px solid rgba(99, 102, 241, 0.3)'
                      }} />
                    </div>
                  )}
                </div>
              </div>
              <div style={{ position: 'relative' }}>
                <button
                  onClick={() => setShowReportModal(true)}
                  style={{
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    padding: 2,
                    color: hoveredTooltip === 'report' ? '#6366f1' : '#6b7280',
                    fontSize: 16,
                    display: 'flex',
                    alignItems: 'center',
                    transition: 'color 200ms'
                  }}
                  onMouseEnter={() => setHoveredTooltip('report')}
                  onMouseLeave={() => setHoveredTooltip(null)}
                >
                  ⚠
                </button>
                {hoveredTooltip === 'report' && (
                  <div style={{
                    position: 'absolute',
                    bottom: '100%',
                    right: 0,
                    marginBottom: 6,
                    padding: '6px 10px',
                    background: 'rgba(17, 24, 39, 0.95)',
                    border: '1px solid rgba(99, 102, 241, 0.3)',
                    borderRadius: 6,
                    color: '#e5e9f5',
                    fontSize: 12,
                    whiteSpace: 'nowrap',
                    zIndex: 1000,
                    pointerEvents: 'none',
                    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.4)'
                  }}>
                    Report AI Inaccuracies
                    <div style={{
                      position: 'absolute',
                      top: '100%',
                      right: 10,
                      width: 0,
                      height: 0,
                      borderLeft: '5px solid transparent',
                      borderRight: '5px solid transparent',
                      borderTop: '5px solid rgba(99, 102, 241, 0.3)'
                    }} />
                  </div>
                )}
              </div>
            </div>
            <input
              ref={sidebarInputRef}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault()
                  handleSubmit()
                } else if (e.key === 'ArrowUp') {
                  e.preventDefault()
                  if (commandHistory.length === 0) return
                  const newIndex = historyIndex === -1 ? commandHistory.length - 1 : Math.max(0, historyIndex - 1)
                  setHistoryIndex(newIndex)
                  setInput(commandHistory[newIndex])
                } else if (e.key === 'ArrowDown') {
                  e.preventDefault()
                  if (historyIndex === -1) return
                  const newIndex = historyIndex + 1
                  if (newIndex >= commandHistory.length) {
                    setHistoryIndex(-1)
                    setInput('')
                  } else {
                    setHistoryIndex(newIndex)
                    setInput(commandHistory[newIndex])
                  }
                }
              }}
              placeholder={pymolMode ? "Enter PyMOL commands (e.g. 'fetch 1ubq')..." : "Ask AI to show molecules..."}
                style={{ 
                  padding: '12px 14px',
                  borderRadius: 8,
                border: pymolMode ? '1px solid rgba(99, 102, 241, 0.4)' : '1px solid rgba(47, 48, 55, 0.6)',
                background: pymolMode ? 'rgba(99, 102, 241, 0.08)' : 'rgba(19, 20, 26, 0.5)',
                  color: '#e5e7eb',
                  fontSize: 14,
                  outline: 'none',
                  transition: 'border-color 200ms'
                }}
                onFocus={(e) => e.currentTarget.style.borderColor = '#4f46e5'}
                onBlur={(e) => e.currentTarget.style.borderColor = 'rgba(47, 48, 55, 0.6)'}
            />
            <button
              onClick={handleSubmit}
              disabled={working}
                style={{ 
                  padding: '12px 14px',
                  borderRadius: 8,
                  border: 'none',
                  background: working ? '#2a2b31' : 'linear-gradient(135deg, #6366f1, #4f46e5)',
                  color: 'white',
                  cursor: working ? 'default' : 'pointer',
                  fontWeight: 600,
                  fontSize: 14,
                  transition: 'transform 100ms',
                  boxShadow: working ? 'none' : '0 2px 8px rgba(99, 102, 241, 0.3)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  minHeight: 40
                }}
                onMouseDown={(e) => !working && (e.currentTarget.style.transform = 'scale(0.98)')}
                onMouseUp={(e) => e.currentTarget.style.transform = 'scale(1)'}
                onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}
            >
              {working ? 'Thinking...' : 'Send'}
            </button>
            </div>
          </div>
        </aside>

        {/* Magic Wand Button - fixed position */}
        {!showPromptPanel && !firstTimeUser && (
          <button
            onClick={() => setShowPromptPanel(true)}
            title="Snorkle viewer chat"
            style={{
              position: 'fixed',
              left: 16,
              top: '50%',
              transform: `translateY(-50%) rotate(${chatButtonRotation}deg)`,
              width: 56,
              height: 56,
              borderRadius: '50%',
              border: '1px solid rgba(200, 200, 200, 0.3)',
              background: '#4338ca',
              backdropFilter: 'blur(10px)',
              color: 'white',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: '0 8px 24px rgba(99, 102, 241, 0.4)',
              transition: 'transform 600ms cubic-bezier(0.34, 1.56, 0.64, 1), box-shadow 200ms',
              zIndex: 50
            }}
            onMouseEnter={(e) => {
              setChatButtonRotation(prev => prev + 360)
              // Remove shadow during rotation to avoid clock hand effect
              const target = e.currentTarget
              target.style.boxShadow = 'none'
              // Restore shadow after animation completes (600ms)
              setTimeout(() => {
                // Check if element still exists before accessing style
                if (target && target.style) {
                  target.style.boxShadow = '0 12px 32px rgba(99, 102, 241, 0.5)'
                }
              }, 600)
            }}
            onMouseLeave={(e) => {
              const target = e.currentTarget
              if (target && target.style) {
                target.style.boxShadow = '0 8px 24px rgba(99, 102, 241, 0.4)'
              }
            }}
          >
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M13 2L3 14h8l-1 8 10-12h-8l1-8z" fill="currentColor"/>
          </svg>
          </button>
        )}

        {/* Viewer - full width */}
        <main 
          ref={viewerContainerRef} 
          style={{ 
            position: 'relative', 
            background: '#0c0d10', 
            flex: 1, 
            width: '100%',
            borderTop: '1px solid rgba(255, 255, 255, 0.05)',
            boxShadow: 'inset 0 8px 16px -8px rgba(99, 102, 241, 0.15), inset 0 4px 8px -4px rgba(139, 92, 246, 0.1)'
          }}
          onClick={() => {
            // When user clicks anywhere in the viewer area, set a flag
            // If the subscribeClickInfo callback fires, it will clear this flag
            // If it doesn't fire within 150ms, we know it was a background click
            iframeClickDetectedRef.current = true
            
            // Check after a short delay if the click was handled by the molecule
            setTimeout(() => {
              if (iframeClickDetectedRef.current) {
                // Click was NOT handled by subscribeClickInfo, so it's a background click
                iframeClickDetectedRef.current = false
                
                // Clear labels and selection
                ;(async () => {
                  try {
                    await sendAndWait({ description: 'RemoveAsset', target: { name: activeLabelNameRef.current } })
                    await sendAndWait({ 
                      description: 'SetSelection',
                      target: { name: currentAssetNameRef.current || 'PDBAsset' },
                      params: { result: {} }
                    })
                    setLastClickInfo(null)
                  } catch (e) {
                    // Ignore errors
                  }
                })()
              }
            }, 150)
          }}
        >
          <iframe
            ref={iframeRef}
            title="viewer"
            src={VIEWER_URL}
            onLoad={() => setLoaded(true)}
            style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', border: 'none' }}
          />
          
          {/* Centered Loader Overlay */}
          {working && (
            <div style={{
                position: 'absolute',
                inset: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: 'rgba(0, 0, 0, 0.3)',
                backdropFilter: 'blur(4px)',
                WebkitBackdropFilter: 'blur(4px)',
                zIndex: 50,
                pointerEvents: 'none'
              }}>
                <div className="thinking-loader" style={{
                  width: '60px',
                  height: '60px'
                }}></div>
              </div>
          )}
          
          {/* WebGL Error Overlay */}
          {webglError && (
            <div style={{
                position: 'absolute',
                inset: 0,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                background: 'rgba(17, 24, 39, 0.98)',
                backdropFilter: 'blur(12px)',
                WebkitBackdropFilter: 'blur(12px)',
                zIndex: 100,
                padding: '2rem',
                color: 'white',
                textAlign: 'center'
              }}>
                <div style={{
                  maxWidth: '600px',
                  background: 'rgba(239, 68, 68, 0.1)',
                  border: '2px solid rgba(239, 68, 68, 0.3)',
                  borderRadius: '16px',
                  padding: '2rem',
                  boxShadow: '0 20px 60px rgba(0, 0, 0, 0.4)'
                }}>
                  <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>⚠️</div>
                  <h2 style={{ fontSize: '1.5rem', marginBottom: '1rem', color: '#EF4444' }}>
                    WebGL Initialization Failed
                  </h2>
                  <p style={{ fontSize: '1rem', marginBottom: '1.5rem', color: '#D1D5DB' }}>
                    The 3D molecular viewer requires WebGL, but your browser failed to initialize it.
                    This usually happens in Safari when hardware acceleration is disabled or unavailable.
                  </p>
                  
                  {/Safari/.test(navigator.userAgent) && (
                    <div style={{
                      background: 'rgba(99, 102, 241, 0.1)',
                      border: '1px solid rgba(99, 102, 241, 0.3)',
                      borderRadius: '12px',
                      padding: '1.5rem',
                      marginBottom: '1.5rem',
                      textAlign: 'left'
                    }}>
                      <h3 style={{ fontSize: '1.1rem', marginBottom: '0.75rem', color: '#818CF8' }}>
                        Safari Users:
                      </h3>
                      <ul style={{ 
                        paddingLeft: '1.5rem',
                        margin: 0,
                        fontSize: '0.95rem',
                        color: '#D1D5DB',
                        lineHeight: '1.6'
                      }}>
                        <li style={{ marginBottom: '0.5rem' }}>
                          Try using <strong>Chrome</strong> or <strong>Firefox</strong> instead
                        </li>
                        <li style={{ marginBottom: '0.5rem' }}>
                          Ensure Safari is updated to the latest version
                        </li>
                        <li style={{ marginBottom: '0.5rem' }}>
                          Check Safari <strong>Preferences → Advanced → WebGL</strong>
                        </li>
                        <li style={{ marginBottom: '0.5rem' }}>
                          Try disabling Safari extensions that might block WebGL
                        </li>
                      </ul>
                    </div>
                  )}
                  
                  <button
                    onClick={() => window.location.reload()}
                    style={{
                      padding: '0.75rem 1.5rem',
                      fontSize: '1rem',
                      fontWeight: 'bold',
                      color: 'white',
                      background: 'linear-gradient(135deg, #6366F1, #8B5CF6)',
                      border: 'none',
                      borderRadius: '8px',
                      cursor: 'pointer',
                      transition: 'transform 0.2s, box-shadow 0.2s',
                      boxShadow: '0 4px 12px rgba(99, 102, 241, 0.4)'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.transform = 'translateY(-2px)';
                      e.currentTarget.style.boxShadow = '0 6px 16px rgba(99, 102, 241, 0.5)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = 'translateY(0)';
                      e.currentTarget.style.boxShadow = '0 4px 12px rgba(99, 102, 241, 0.4)';
                    }}
                  >
                    Reload Page
                  </button>
                </div>
              </div>
          )}
          
          {/* Hover Info Tooltip */}
          {hoverInfo && lastPointerRef.current && (
            <div style={{
              position: 'absolute',
              left: lastPointerRef.current.x + 20,
              top: lastPointerRef.current.y + 20,
              background: 'rgba(17, 24, 39, 0.95)',
              backdropFilter: 'blur(8px)',
              WebkitBackdropFilter: 'blur(8px)',
              border: '1px solid rgba(99, 102, 241, 0.3)',
              borderRadius: 8,
              padding: '8px 12px',
              fontSize: 13,
              color: '#e5e7eb',
              pointerEvents: 'none',
              zIndex: 100,
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.4)',
              whiteSpace: 'nowrap',
              fontFamily: 'ui-monospace, monospace'
            }}>
              {hoverInfo.chain && (
                <div style={{ color: '#60a5fa', marginBottom: 4 }}>
                  Chain: <span style={{ fontWeight: 600 }}>{hoverInfo.chain}</span>
                </div>
              )}
              {hoverInfo.residue !== undefined && (
                <div style={{ color: '#a78bfa' }}>
                  Residue: <span style={{ fontWeight: 600 }}>{hoverInfo.residue}</span>
                </div>
              )}
              {hoverInfo.atom !== undefined && (
                <div style={{ color: '#34d399' }}>
                  Atom: <span style={{ fontWeight: 600 }}>{hoverInfo.atom}</span>
                </div>
              )}
            </div>
          )}
          
          {/* Mouse tracker for pointer position */}
          <div
            onMouseMove={(e) => {
              const rect = viewerContainerRef.current?.getBoundingClientRect()
              if (rect) {
                lastPointerRef.current = { x: e.clientX - rect.left, y: e.clientY - rect.top }
              }
            }}
            style={{
              position: 'absolute',
              inset: 0,
              pointerEvents: 'none',
              zIndex: 5
            }}
          />
          {/* Transparent hover zone at top for settings button */}
          {!showSettingsMenu && !showOptions && !showSequence && !showWorkspace && (
          <div
            onMouseEnter={() => {
              setShowSettingsBtn(true)
              if (settingsHideTimerRef.current) clearTimeout(settingsHideTimerRef.current)
            }}
            onMouseLeave={() => {
              if (settingsHideTimerRef.current) clearTimeout(settingsHideTimerRef.current)
              settingsHideTimerRef.current = setTimeout(() => setShowSettingsBtn(false), 300)
            }}
            style={{
              position: 'absolute',
              top: 80,
              right: 0,
              width: 120,
              height: 72,
              pointerEvents: 'auto',
              zIndex: 10
            }}
          />
          )}
            <button
              onClick={() => { setShowSettingsMenu(true) }}
              title="Settings"
            style={{ 
              position: 'absolute', 
              top: 92, 
              right: 12, 
              width: 44, 
              height: 44, 
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              borderRadius: 12, 
              border: '1px solid rgba(99,102,241,0.25)', 
              background: 'linear-gradient(135deg, rgba(20,22,30,0.92), rgba(15,17,25,0.92))',
              backdropFilter: 'blur(10px)',
              color: '#a5b4fc', 
              cursor: 'pointer',
              boxShadow: '0 4px 16px rgba(0,0,0,0.3)',
              transition: showSettingsFlash ? 'none' : 'all 300ms cubic-bezier(0.4, 0, 0.2, 1)',
              opacity: (showSettingsBtn || showSettingsFlash) && !showOptions && !showSequence && !showWorkspace && !showSettingsMenu ? 1 : 0,
              transform: ((showSettingsBtn || showSettingsFlash) && !showOptions && !showSequence && !showWorkspace && !showSettingsMenu) ? 'translateY(0) scale(1)' : 'translateY(-4px) scale(0.95)',
              pointerEvents: ((showSettingsBtn || showSettingsFlash) && !showOptions && !showSequence && !showWorkspace && !showSettingsMenu) ? 'auto' : 'none',
              zIndex: 20,
              animation: showSettingsFlash ? 'settingsFlash 1.5s ease-in-out infinite' : 'none'
            }}
            onMouseEnter={(e) => {
              // Keep the button visible while hovering
              setShowSettingsBtn(true)
              if (settingsHideTimerRef.current) clearTimeout(settingsHideTimerRef.current)
              // Style updates (only if not flashing)
              if (!showSettingsFlash) {
              e.currentTarget.style.background = 'linear-gradient(135deg, rgba(30,32,42,0.95), rgba(20,22,30,0.95))'
              e.currentTarget.style.borderColor = 'rgba(99,102,241,0.4)'
              e.currentTarget.style.transform = 'translateY(0) scale(1.05)'
              e.currentTarget.style.boxShadow = '0 6px 24px rgba(99,102,241,0.25)'
              }
            }}
            onMouseLeave={(e) => {
              // Fade out after a delay when mouse leaves the button
              if (settingsHideTimerRef.current) clearTimeout(settingsHideTimerRef.current)
              settingsHideTimerRef.current = setTimeout(() => setShowSettingsBtn(false), 500)
              // Style updates (only if not flashing)
              if (!showSettingsFlash) {
              e.currentTarget.style.background = 'linear-gradient(135deg, rgba(20,22,30,0.92), rgba(15,17,25,0.92))'
              e.currentTarget.style.borderColor = 'rgba(99,102,241,0.25)'
              e.currentTarget.style.transform = 'translateY(0) scale(1)'
              e.currentTarget.style.boxShadow = '0 4px 16px rgba(0,0,0,0.3)'
              }
            }}
          >
            <div style={{ 
              animation: showSettingsFlash ? 'settingsGlow 1.5s ease-in-out infinite' : 'none',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <circle cx="12" cy="12" r="3" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
              <path d="M12 2v2m0 16v2M4.93 4.93l1.41 1.41m11.32 11.32l1.41 1.41M2 12h2m16 0h2M4.93 19.07l1.41-1.41m11.32-11.32l1.41-1.41" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
            </svg>
            </div>
            </button>
          
          {/* Tooltip that shows on hover or during flash (but not when menu is open) */}
          {(showSettingsBtn || showSettingsFlash) && !showSettingsMenu && (
            <div
              style={{
                position: 'absolute',
                top: 144,
                right: 12,
                color: '#e5e9f5',
                fontSize: 13,
                fontWeight: 600,
                textShadow: '0 2px 8px rgba(0, 0, 0, 0.8), 0 0 20px rgba(99, 102, 241, 0.6)',
                zIndex: 18,
                pointerEvents: 'none',
                whiteSpace: 'nowrap',
                letterSpacing: '0.3px',
                animation: showSettingsFlash ? 'settingsGlow 1.5s ease-in-out infinite' : 'none',
                opacity: showSettingsFlash ? 1 : (showSettingsBtn ? 0.95 : 0),
                transition: showSettingsFlash ? 'none' : 'opacity 150ms ease-out'
              }}
            >
              View Settings
            </div>
          )}
          
          <div
            style={{ position: 'absolute', top: 144, right: 0, width: 260, pointerEvents: showSettingsMenu ? 'auto' : 'none', zIndex: 20 }}
          >
            <div
              style={{ opacity: showSettingsMenu ? 1 : 0, transform: `translateY(${showSettingsMenu ? 0 : -8}px)`, transition: 'opacity 200ms cubic-bezier(0.4, 0, 0.2, 1), transform 200ms cubic-bezier(0.4, 0, 0.2, 1)', background: 'linear-gradient(135deg, rgba(15,17,23,0.97), rgba(20,22,30,0.97))', backdropFilter: 'blur(12px)', border: '1px solid rgba(99,102,241,0.15)', borderRadius: 12, boxShadow: '0 12px 32px rgba(0,0,0,0.4), 0 0 0 1px rgba(255,255,255,0.05) inset', padding: 14, display: 'grid', gap: 10 }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                <strong style={{ color: '#f0f4ff', fontSize: 14, fontWeight: 600, letterSpacing: 0.4 }}>Settings</strong>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                  <button 
                    onClick={async () => { 
                      setShowSettingsMenu(false);
                      
                      // Get current scene state to find actual asset names
                      const state = await querySceneState();
                      
                      // Remove all assets from the scene
                      if (state?.assets && Array.isArray(state.assets)) {
                        if (state.assets.length === 0) {
                        } else {
                          for (const asset of state.assets) {
                            const assetName = asset.name || asset.baseAssetName;
                            if (assetName) {
                              try {
                                await sendAndWait({ description: 'RemoveAsset', target: { name: assetName } });
                              } catch (err) {
                              }
                            }
                          }
                        }
                      } else {
                      }
                      
                      // Clear all UI state
                      setLoadedAssets([]);
                      setActiveMolecule(null);
                      currentAssetNameRef.current = 'PDBAsset'; // Reset to default
                      setHoverInfo(null);
                      setLastClickInfo(null);
                      setStructure(null);
                      setShowSequence(false);
                    }}
                    title="Clear Workspace"
                    style={{ padding: '6px 8px', borderRadius: 8, border: '1px solid rgba(239,68,68,0.3)', background: 'rgba(30,32,42,0.6)', color: '#ef4444', cursor: 'pointer', fontSize: 12, fontWeight: 500, transition: 'all 150ms ease', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                    onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(239,68,68,0.15)'; e.currentTarget.style.borderColor = 'rgba(239,68,68,0.5)' }}
                    onMouseLeave={(e) => { e.currentTarget.style.background = 'rgba(30,32,42,0.6)'; e.currentTarget.style.borderColor = 'rgba(239,68,68,0.3)' }}
                  >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M3 6h18M8 6V4a1 1 0 011-1h6a1 1 0 011 1v2M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6M10 11v6M14 11v6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </button>
                  <CloseButton 
                    onClick={() => setShowSettingsMenu(false)} 
                    windowName='Setings'
                    />
                </div>
              </div>
              <button
                onClick={() => { setShowSettingsMenu(false); setShowOptions(true); setShowSequence(false); setShowWorkspace(false); void refreshOverlay() }}
                style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 14px', borderRadius: 10, border: '1px solid #2f3037', background: 'linear-gradient(135deg, rgba(20,22,28,0.98), rgba(14,16,20,0.98))', color: '#e5e7eb', cursor: 'pointer', transition: 'transform 120ms ease, box-shadow 120ms ease' }}
                onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.boxShadow = '0 8px 18px rgba(0,0,0,0.35)'; (e.currentTarget as HTMLButtonElement).style.transform = 'translateY(-1px)' }}
                onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.boxShadow = 'none'; (e.currentTarget as HTMLButtonElement).style.transform = 'none' }}
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ opacity: 0.9 }}>
                  <path d="M4 7h12M4 12h16M4 17h10" stroke="#8ab4ff" strokeWidth="2" strokeLinecap="round"/>
                </svg>
                <span style={{ fontWeight: 700, letterSpacing: 0.3 }}>Aesthetics</span>
                <span style={{ marginLeft: 'auto', opacity: 0.6, fontSize: 12 }}>Open viewer options</span>
              </button>
              <button
                onClick={() => { setShowSettingsMenu(false); setShowSequence(true); setShowOptions(false); setShowWorkspace(false); void loadStructure() }}
                style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 14px', borderRadius: 10, border: '1px solid #2f3037', background: 'linear-gradient(135deg, rgba(20,22,28,0.98), rgba(14,16,20,0.98))', color: '#e5e7eb', cursor: 'pointer', transition: 'transform 120ms ease, box-shadow 120ms ease' }}
                onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.boxShadow = '0 8px 18px rgba(0,0,0,0.35)'; (e.currentTarget as HTMLButtonElement).style.transform = 'translateY(-1px)' }}
                onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.boxShadow = 'none'; (e.currentTarget as HTMLButtonElement).style.transform = 'none' }}
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ opacity: 0.9 }}>
                  <path d="M4 6c4 0 4 12 8 12s4-12 8-12" stroke="#67e8f9" strokeWidth="2" strokeLinecap="round"/>
                </svg>
                <span style={{ fontWeight: 700, letterSpacing: 0.3 }}>Sequence</span>
                <span style={{ marginLeft: 'auto', opacity: 0.6, fontSize: 12 }}>Show sequence view</span>
              </button>
              <button
                onClick={async () => { 
                  setShowSettingsMenu(false); 
                  setShowWorkspace(true);
                  setShowOptions(false);
                  setShowSequence(false);
                  // Refresh scene state when opening workspace
                  const state = await querySceneState();
                  setSceneState(state);
                }}
                style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 14px', borderRadius: 10, border: '1px solid #2f3037', background: 'linear-gradient(135deg, rgba(20,22,28,0.98), rgba(14,16,20,0.98))', color: '#e5e7eb', cursor: 'pointer', transition: 'transform 120ms ease, box-shadow 120ms ease' }}
                onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.boxShadow = '0 8px 18px rgba(0,0,0,0.35)'; (e.currentTarget as HTMLButtonElement).style.transform = 'translateY(-1px)' }}
                onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.boxShadow = 'none'; (e.currentTarget as HTMLButtonElement).style.transform = 'none' }}
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ opacity: 0.9 }}>
                  <path d="M3 7h18M3 12h18M3 17h18" stroke="#a78bfa" strokeWidth="2" strokeLinecap="round"/>
                  <circle cx="7" cy="7" r="1.5" fill="#a78bfa"/>
                  <circle cx="7" cy="12" r="1.5" fill="#a78bfa"/>
                  <circle cx="7" cy="17" r="1.5" fill="#a78bfa"/>
                </svg>
                <span style={{ fontWeight: 700, letterSpacing: 0.3 }}>Workspace</span>
                <span style={{ marginLeft: 'auto', opacity: 0.6, fontSize: 12 }}>Manage molecules</span>
              </button>
              <button
                disabled={disableSaveView}
                onClick={async () => {
                  setDisableSaveView(true);
                  setShowSettingsMenu(false);
                  
                  // Get complete scene state
                  const sceneState = await querySceneState();
                  
                  
                  // Filter to only primary assets (exclude viewer-generated clones)
                  // Primary assets: name === baseAssetName (e.g., CID_5793, PDB_1UBQ)
                  // Viewer clones: name !== baseAssetName (e.g., CID_5793_0, CID_5793_1)
                  const primaryAssets = (sceneState?.assets || []).filter((asset: any) => 
                    asset.name === asset.baseAssetName
                  );

                  const result = await sendAndWait({ description: 'GetCurrentView' })
                  if (result.errors) {
                    setDisableSaveView(false);
                    console.error('Error getting scene state:', result.errors);
                    return;
                  }
                  const view = result.result;
                  const camObj = view.cameraPosition ?? view.camera ?? view.cameraPos ?? view.camPos ?? null;
                  const propLookup: Record<string, any> = {};
                  const infoLookup: Record<string, any> = {};
                  for (const asset of view.renderables) {
                    propLookup[asset.name] = asset.properties;
                    infoLookup[asset.name] = asset.information;
                  }
                                  
                  // Create shareable export with all necessary information
                  const shareableState = {
                    version: '1.0.0',
                    type: 'molecular-viewer',
                    timestamp: new Date().toISOString(),
                    assets: primaryAssets.map((asset: any) => ({
                      name: asset.name || asset.baseAssetName,
                      type: asset.type,
                      source: asset.source,
                      properties: propLookup[asset.name] || asset.properties || {},
                      information: infoLookup[asset.name] || asset.information || {}
                    })),
                    camera: {
                      camPos: camObj?.cameraPos ?? camObj?.camPos ?? null,
                      lookAt: camObj?.lookAt ?? null,
                      upVector: camObj?.upVector ?? null
                    },
                    selection: sceneState?.selection || null,
                    background: sceneState?.background || null,
                    autoRotate: rotationPeriodRef.current && rotationPeriodRef.current > 0,
                    rotationPeriod: rotationPeriodRef.current && rotationPeriodRef.current > 0 ? rotationPeriodRef.current : null,
                    metadata: {
                      loadedAssets: loadedAssets.map(a => ({
                        source: a.source,
                        id: a.id
                      })),
                      assetCount: primaryAssets.length,
                      totalAssetsIncludingClones: (sceneState?.assets || []).length
                    }
                  };
                  
                  
                  // Log each asset's basic info to verify all are included
                  shareableState.assets.forEach((asset: any, idx: number) => {
                  });
                  
                  const jsonString = JSON.stringify(shareableState, null, 2);
                  
                  
                  // Verify JSON structure by parsing it back
                  try {
                    const parsed = JSON.parse(jsonString);
                  } catch (e) {
                    setDisableSaveView(false);
                    return;
                  }
                  const name_list = primaryAssets.map((asset: any) => asset.name);
                  const protein_names= name_list.filter((name: string) => name.startsWith('PDB_'));
                  const pubchem_names= name_list.filter((name: string) => name.startsWith('CID_'));
                  let nameParts = [];
                  if (protein_names.length > 0) {
                    nameParts.push(protein_names.length > 1 ? `${protein_names.length} Proteins` : protein_names[0].substring(4));
                  }
                  if (pubchem_names.length > 0) {
                    nameParts.push(pubchem_names.length > 1 ? `${pubchem_names.length} Compounds` : pubchem_names[0].substring(4));
                  }
                  const name = nameParts.join(' & ');
                  const description = name_list.map((name: string) => name.replace("_", " ")).join(', ');
                  APIWebVisualization(
                    'fake-id',
                    'molecular-viewer',
                    name,
                    description,
                    (response: any) => {
                      setDisableSaveView(false);
                    },
                    (response: any) => {
                      setDisableSaveView(false);
                    },
                    false,
                    {saved_view: jsonString}
                  );
                }}
                style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 14px', borderRadius: 10, border: '1px solid #2f3037', background: disableSaveView ? '#333333' : 'linear-gradient(135deg, rgba(20,22,28,0.98), rgba(14,16,20,0.98))', color: '#e5e7eb', cursor: disableSaveView ? 'not-allowed' : 'pointer', transition: 'transform 120ms ease, box-shadow 120ms ease' }}
                onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.boxShadow = '0 8px 18px rgba(0,0,0,0.35)'; (e.currentTarget as HTMLButtonElement).style.transform = 'translateY(-1px)' }}
                onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.boxShadow = 'none'; (e.currentTarget as HTMLButtonElement).style.transform = 'none' }}
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ opacity: 0.9 }}>
                  <path d="M22 2L11 13M22 2l-7 20-4-9-9-4 20-7z" stroke="#fbbf24" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                <span style={{ fontWeight: 700, letterSpacing: 0.3 }}>Share Scene</span>
                <span style={{ marginLeft: 'auto', opacity: 0.6, fontSize: 12 }}>Share visualization</span>
              </button>
            </div>
          </div>
          {showOverlay && (
            <pre
              style={{ position: 'absolute', top: 8, left: 8, margin: 0, padding: '8px 10px', background: 'rgba(0,0,0,0.5)', color: '#e5e7eb', fontSize: 12, borderRadius: 6, pointerEvents: 'none', maxWidth: '40%', whiteSpace: 'pre-wrap' }}
            >{overlayText}</pre>
          )}
          {/* Click label is shown directly on the molecule via ClickLabel asset in the viewer - no need for UI display */}
          {/* suggestions hidden per request to remove UI bubbles */}
          {showOptions && (
            <div style={{ position: 'absolute', right: 0, top: `calc(${headerHeight()}px + 0.75em)`, bottom: 0, width: 380, background: 'linear-gradient(135deg, rgba(12,14,20,0.97), rgba(16,18,26,0.97))', backdropFilter: 'blur(20px)', border: '1px solid rgba(99,102,241,0.15)', borderRadius: 16, boxShadow: '0 20px 60px rgba(0,0,0,0.5), 0 0 0 1px rgba(255,255,255,0.05) inset' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '18px 20px', borderBottom: '1px solid rgba(99,102,241,0.1)', background: 'linear-gradient(180deg, rgba(20,22,30,0.6), rgba(15,17,25,0.3))' }}>
                <strong style={{ color: '#f0f4ff', fontSize: 15, fontWeight: 600, letterSpacing: 0.5, textShadow: '0 2px 8px rgba(0,0,0,0.3)' }}>Viewer Options</strong>
                <CloseButton 
                  onClick={() => { setShowOptions(false); setShowSettingsMenu(false); setShowSequence(false); setShowWorkspace(false) }}
                  windowName='Viewer Options'
                  />
              </div>
              <div style={{ padding: '20px', overflowY: 'auto', maxHeight: 'calc(100% - 138px)', overflowX: "hidden"}}>
                <div style={{ display: 'grid', gap: 14 }}>
                {(() => {
                  const info = currentInfo || {}
                  const props = { ...currentProps, ...pendingProps }
                  const items: Array<{ key: string; render: React.ReactElement }> = []
                    // Keys to hide from the UI (including metadata option fields)
                    const hiddenKeys = new Set(['uniformModelColor', 'uniformSurfaceColor', 'visible'])
                  // Gather options by key from information fields ending with 'Options'
                  const optionsByKey: Record<string, any[]> = {}
                    
                    // First, parse options from the information object
                  for (const k of Object.keys(info)) {
                      if (k.endsWith('Options')) {
                      const base = k.slice(0, -'Options'.length)
                        const rawOptions = (info as any)[k]
                        // Handle both array and comma-separated string formats
                        if (Array.isArray(rawOptions)) {
                          optionsByKey[base] = rawOptions
                        } else if (typeof rawOptions === 'string') {
                          optionsByKey[base] = rawOptions.split(',').map((s: string) => s.trim())
                        }
                      }
                    }
                    
                    // Second, parse options from properties that end in 'Options' (like surfaceColorSchemeOptions)
                    for (const k of Object.keys(currentProps || {})) {
                      if (k.endsWith('Options')) {
                        const base = k.slice(0, -'Options'.length)
                        const rawOptions = (currentProps as any)[k]
                        // Hide the options property itself from the UI
                        hiddenKeys.add(k)
                        // Parse the options for the base property
                        if (Array.isArray(rawOptions)) {
                          optionsByKey[base] = rawOptions
                        } else if (typeof rawOptions === 'string') {
                          optionsByKey[base] = rawOptions.split(',').map((s: string) => s.trim())
                        }
                      }
                    }
                    
                  // Determine all editable keys: union of current props and any keys that have options
                  const allKeysSet = new Set<string>([...Object.keys(currentProps || {}), ...Object.keys(optionsByKey)])
                    const allKeys = Array.from(allKeysSet).sort().filter(k => !hiddenKeys.has(k))
                  for (const key of allKeys) {
                    const label = key
                    const val = (props as any)[key]
                      // Check if this key has associated options (highest priority)
                      const options = optionsByKey[key] || []
                      
                      if (options.length > 0) {
                        // Render as dropdown when options are available
                      items.push({ key, render: (
                          <label style={{ color: '#e5e9f5', fontSize: 13, display: 'grid', gap: 8 }}>
                            <span style={{ opacity: 0.9, letterSpacing: 0.3, fontWeight: 500, color: '#c7d2fe' }}>{label}</span>
                          <select
                            value={val ?? ''}
                            onChange={(e) => setPendingProps((pp) => ({ ...pp, [key]: e.target.value }))}
                            style={{
                                boxSizing: 'border-box', width: '100%', padding: '11px 12px', background: 'linear-gradient(180deg, rgba(15,17,25,0.9), rgba(10,12,18,0.9))', color: '#e5e9f5',
                                border: '1px solid rgba(99,102,241,0.2)', borderRadius: 10, outline: 'none', boxShadow: '0 2px 8px rgba(0,0,0,0.15), inset 0 0 0 1px rgba(255,255,255,0.03)',
                                fontSize: 13, cursor: 'pointer', transition: 'all 200ms ease'
                            }}
                              onFocus={(e) => { e.currentTarget.style.borderColor = 'rgba(99,102,241,0.5)'; e.currentTarget.style.boxShadow = '0 4px 12px rgba(99,102,241,0.2), inset 0 0 0 1px rgba(99,102,241,0.1)' }}
                              onBlur={(e) => { e.currentTarget.style.borderColor = 'rgba(99,102,241,0.2)'; e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.15), inset 0 0 0 1px rgba(255,255,255,0.03)' }}
                          >
                            <option value="" disabled>select…</option>
                            {options.map((opt, i) => (<option key={i} value={opt}>{String(opt)}</option>))}
                          </select>
                        </label>
                      )})
                      } else if (typeof val === 'boolean') {
                      items.push({ key, render: (
                          <label style={{ color: '#e5e9f5', fontSize: 13, display: 'flex', alignItems: 'center', gap: 12, cursor: 'pointer', padding: '4px 0' }}>
                          <input
                            type="checkbox"
                            checked={!!val}
                            onChange={(e) => setPendingProps((pp) => ({ ...pp, [key]: e.target.checked }))}
                              style={{ width: 20, height: 20, accentColor: '#6366f1', cursor: 'pointer' }}
                          />
                            <span style={{ opacity: 0.9, letterSpacing: 0.3, fontWeight: 500, color: '#c7d2fe' }}>{label}</span>
                        </label>
                      )})
                      } else if (typeof val === 'number') {
                      items.push({ key, render: (
                          <label style={{ color: '#e5e9f5', fontSize: 13, display: 'grid', gap: 8 }}>
                            <span style={{ opacity: 0.9, letterSpacing: 0.3, fontWeight: 500, color: '#c7d2fe' }}>{label}</span>
                          <input
                            type="number" step="0.1" value={val ?? 0}
                            onChange={(e) => setPendingProps((pp) => ({ ...pp, [key]: parseFloat(e.target.value) }))}
                              style={{ boxSizing: 'border-box', width: '100%', padding: '11px 12px', background: 'linear-gradient(180deg, rgba(15,17,25,0.9), rgba(10,12,18,0.9))', color: '#e5e9f5', border: '1px solid rgba(99,102,241,0.2)', borderRadius: 10, outline: 'none', fontSize: 13, boxShadow: '0 2px 8px rgba(0,0,0,0.15), inset 0 0 0 1px rgba(255,255,255,0.03)', transition: 'all 200ms ease' }}
                              onFocus={(e) => { e.currentTarget.style.borderColor = 'rgba(99,102,241,0.5)'; e.currentTarget.style.boxShadow = '0 4px 12px rgba(99,102,241,0.2), inset 0 0 0 1px rgba(99,102,241,0.1)' }}
                              onBlur={(e) => { e.currentTarget.style.borderColor = 'rgba(99,102,241,0.2)'; e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.15), inset 0 0 0 1px rgba(255,255,255,0.03)' }}
                          />
                        </label>
                      )})
                      } else if (typeof val === 'string' || val === undefined || val === null) {
                        // Only render text input for actual string values or undefined values without options
                      items.push({ key, render: (
                          <label style={{ color: '#e5e9f5', fontSize: 13, display: 'grid', gap: 8 }}>
                            <span style={{ opacity: 0.9, letterSpacing: 0.3, fontWeight: 500, color: '#c7d2fe' }}>{label}</span>
                          <input
                            type="text" value={val ?? ''}
                            onChange={(e) => setPendingProps((pp) => ({ ...pp, [key]: e.target.value }))}
                              style={{ boxSizing: 'border-box', width: '100%', padding: '11px 12px', background: 'linear-gradient(180deg, rgba(15,17,25,0.9), rgba(10,12,18,0.9))', color: '#e5e9f5', border: '1px solid rgba(99,102,241,0.2)', borderRadius: 10, outline: 'none', fontSize: 13, boxShadow: '0 2px 8px rgba(0,0,0,0.15), inset 0 0 0 1px rgba(255,255,255,0.03)', transition: 'all 200ms ease' }}
                              onFocus={(e) => { e.currentTarget.style.borderColor = 'rgba(99,102,241,0.5)'; e.currentTarget.style.boxShadow = '0 4px 12px rgba(99,102,241,0.2), inset 0 0 0 1px rgba(99,102,241,0.1)' }}
                              onBlur={(e) => { e.currentTarget.style.borderColor = 'rgba(99,102,241,0.2)'; e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.15), inset 0 0 0 1px rgba(255,255,255,0.03)' }}
                          />
                        </label>
                      )})
                    }
                    // Skip complex objects/arrays for now
                  }
                  return (
                    <>
                      {items.map(it => (
                          <div key={it.key} style={{ background: 'linear-gradient(135deg, rgba(22,24,34,0.5), rgba(18,20,28,0.5))', border: '1px solid rgba(99,102,241,0.1)', borderRadius: 12, padding: '14px 16px', boxShadow: '0 2px 8px rgba(0,0,0,0.1)', transition: 'all 200ms ease' }}
                            onMouseEnter={(e) => { (e.currentTarget as HTMLDivElement).style.background = 'linear-gradient(135deg, rgba(28,30,42,0.6), rgba(22,24,34,0.6))'; (e.currentTarget as HTMLDivElement).style.borderColor = 'rgba(99,102,241,0.2)' }}
                            onMouseLeave={(e) => { (e.currentTarget as HTMLDivElement).style.background = 'linear-gradient(135deg, rgba(22,24,34,0.5), rgba(18,20,28,0.5))'; (e.currentTarget as HTMLDivElement).style.borderColor = 'rgba(99,102,241,0.1)' }}
                          >
                          {it.render}
                        </div>
                      ))}
                      </>
                    )
                  })()}
                </div>
              </div>
              <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, display: 'flex', gap: 12, padding: '16px 20px', borderTop: '1px solid rgba(99,102,241,0.1)', background: 'linear-gradient(180deg, rgba(15,17,25,0.3), rgba(20,22,30,0.6))', backdropFilter: 'blur(10px)' }}>
                        <button
                          onClick={() => setPendingProps({})}
                  style={{ flex: 1, padding: '12px 16px', borderRadius: 11, border: '1px solid rgba(99,102,241,0.2)', background: 'linear-gradient(180deg, rgba(30,32,42,0.6), rgba(22,24,34,0.8))', color: '#a5b4fc', fontSize: 13, fontWeight: 600, cursor: 'pointer', transition: 'all 200ms cubic-bezier(0.4, 0, 0.2, 1)', boxShadow: '0 2px 6px rgba(0,0,0,0.15)' }}
                  onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.background = 'linear-gradient(180deg, rgba(40,42,52,0.7), rgba(32,34,44,0.9))'; (e.currentTarget as HTMLButtonElement).style.transform = 'translateY(-1px)'; (e.currentTarget as HTMLButtonElement).style.boxShadow = '0 4px 12px rgba(0,0,0,0.25)' }}
                  onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.background = 'linear-gradient(180deg, rgba(30,32,42,0.6), rgba(22,24,34,0.8))'; (e.currentTarget as HTMLButtonElement).style.transform = 'translateY(0)'; (e.currentTarget as HTMLButtonElement).style.boxShadow = '0 2px 6px rgba(0,0,0,0.15)' }}
                        >
                          Reset
                        </button>
                        <button
                          onClick={() => void applySuggestion(pendingProps)}
                  style={{ flex: 1, padding: '12px 16px', borderRadius: 11, border: '1px solid rgba(99,102,241,0.4)', background: 'linear-gradient(135deg, #6366f1, #4f46e5)', color: '#ffffff', fontSize: 13, fontWeight: 600, cursor: 'pointer', transition: 'all 200ms cubic-bezier(0.4, 0, 0.2, 1)', boxShadow: '0 4px 12px rgba(99,102,241,0.3)' }}
                  onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.background = 'linear-gradient(135deg, #7c3aed, #6366f1)'; (e.currentTarget as HTMLButtonElement).style.transform = 'translateY(-1px)'; (e.currentTarget as HTMLButtonElement).style.boxShadow = '0 6px 20px rgba(99,102,241,0.45)' }}
                  onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.background = 'linear-gradient(135deg, #6366f1, #4f46e5)'; (e.currentTarget as HTMLButtonElement).style.transform = 'translateY(0)'; (e.currentTarget as HTMLButtonElement).style.boxShadow = '0 4px 12px rgba(99,102,241,0.3)' }}
                        >
                          Apply
                        </button>
              </div>
            </div>
          )}
          {showSequence && (
            <div style={{ 
                position: 'absolute', 
                top: `calc(${headerHeight()}px + 0.75em)`,
                right: 0, 
                bottom: 0, 
                maxWidth: '25%',
                background: 'linear-gradient(to bottom, #0a0b0f, #0f1117)', 
                borderTop: '2px solid rgba(99, 102, 241, 0.3)', 
                boxShadow: '0 -8px 32px rgba(99, 102, 241, 0.15), 0 -4px 16px rgba(0,0,0,0.4)', 
                padding: 16,
                display: 'flex',
                flexDirection: 'column',
                gap: 12,
                zIndex: 50
              }}>
              {/* Header */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexShrink: 0 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M4 6c4 0 4 12 8 12s4-12 8-12" stroke="#67e8f9" strokeWidth="2.5" strokeLinecap="round"/>
                  </svg>
                  <strong style={{ color: '#e5e7eb', fontSize: 16, fontWeight: 600, letterSpacing: 0.3 }}>Sequence Viewer</strong>
                </div>
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, color: '#9ca3af', fontSize: 13 }}>
                  <span>Chain</span>
                  <select 
                    value={String(chainIdx)} 
                    onChange={(e) => setChainIdx(parseInt(e.target.value))} 
                    style={{ 
                      padding: '8px 12px', 
                      background: 'rgba(17, 19, 24, 0.8)', 
                      color: '#e5e7eb', 
                      border: '1px solid #3f3f46', 
                      borderRadius: 8,
                      cursor: 'pointer',
                      fontSize: 13,
                      fontWeight: 500,
                      transition: 'all 150ms ease'
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.borderColor = '#6366f1'}
                    onMouseLeave={(e) => e.currentTarget.style.borderColor = '#3f3f46'}
                  >
                    {structure?.map((c: any, i: number) => (
                      <option key={i} value={i}>{`Chain ${c?.chain ?? i}`}</option>
                    ))}
                  </select>
                </label>
                <div style={{ flex: 1 }} />
                <CloseButton 
                  onClick={() => { setShowSequence(false); setShowOptions(false); setShowSettingsMenu(false); setShowWorkspace(false) }} 
                  windowName='Sequence Viewer'
                  />
              </div>

              {/* Content */}
              {!structure && (
                <div style={{ 
                  color: '#9ca3af', 
                  fontSize: 14, 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'center',
                  padding: 40,
                  fontStyle: 'italic'
                }}>
                  No sequence data available.
                </div>
              )}
              {structure && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 12, flex: 1, minHeight: 0, overflow: "auto" }}>
                  {(() => {
                    const chain = structure[chainIdx] || {}
                    const residues: any[] = Array.isArray(chain?.residues) ? chain.residues : []
                    return (
                      <>
                        {/* Info bar */}
                        <div style={{ 
                          display: 'flex', 
                          alignItems: 'center', 
                          gap: 12, 
                          padding: '8px 12px',
                          background: 'rgba(99, 102, 241, 0.08)',
                          borderRadius: 8,
                          border: '1px solid rgba(99, 102, 241, 0.2)',
                          flexShrink: 0
                        }}>
                          <span style={{ color: '#a5b4fc', fontSize: 13, fontWeight: 500 }}>
                            {residues.reduce((acc, r) => acc + (r.missing ? 0 : 1), 0)} of {residues.length} {residues.length === 1 ? 'residue' : 'residues'} present
                          </span>
                          </div>

                        {/* Residue buttons - WRAPPED */}
                        <div style={{ 
                          display: 'flex', 
                          flexWrap: 'wrap',
                          alignItems: 'flex-start',
                          gap: 6,
                          overflow: 'auto',
                          padding: 4
                        }}>
                          {residues.map((r, idx) => ((r.missing ? 
                          <button key={idx} disabled style={{
                            padding: '2px 7px',
                            minWidth: 20,
                            borderRadius: 7,
                            border: '1px solid rgba(48, 48, 58, 0.3)',
                            backgroundColor: 'rgba(139, 148, 167, 0.9))',
                            color: '#e5e7eb',
                            cursor: 'default',
                            fontSize: 12,
                            fontWeight: 600,
                            fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
                            letterSpacing: 0.5,
                            transform: 'none', 
                            boxShadow: 'none'
                          }}
                            title={`${r?.name || r?.longCode || ''} (Residue ${idx + 1})`}>
                            {r?.shortCode || r?.longCode || r?.name || ''}
                          </button> : (
                            <button 
                              key={idx} 
                              onClick={async () => {
                              const tname = currentAssetNameRef.current || 'PDBAsset'
                                
                                // Focus on the residue (fire and forget - don't wait for response)
                                // Note: SetCameraPosition doesn't support residueId targeting, so we still use FocusOnTarget here
                                // The viewer's internal FocusOnTarget speed cannot be controlled via messaging protocol
                                sendMessage({ 
                                  description: 'FocusOnTarget', 
                                  target: { name: tname }, 
                                  result: { residueId: r?.id, isolateRadius: 4, cameraMode: 'lookAt', fogColor: 'background', duration: 0.5 } 
                                })
                                
                                // Highlight the residue using SetSelection
                                if (r?.id !== undefined && r?.id !== null) {
                                  try {
                                    const selectResult = await sendAndWait({
                                      description: 'SetSelection',
                                      target: { name: tname },
                                      result: { residue: r.id }
                                    })
                                  } catch (err) {
                                  }
                                } else {
                                }
                              }} 
                              style={{ 
                                padding: '2px 7px',
                                minWidth: 20,
                                borderRadius: 7,
                                border: hoveredResidueId === r?.id 
                                  ? '1px solid #6366f1' 
                                  : '1px solid rgba(99, 102, 241, 0.3)',
                                background: hoveredResidueId === r?.id
                                  ? 'linear-gradient(135deg, rgba(99, 102, 241, 0.3), rgba(79, 70, 229, 0.3))'
                                  : 'linear-gradient(135deg, rgba(31, 41, 55, 0.9), rgba(17, 24, 39, 0.9))',
                                color: '#e5e7eb',
                                cursor: 'pointer',
                                fontSize: 12,
                                fontWeight: 600,
                                fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
                                letterSpacing: 0.5,
                                transition: 'all 120ms ease',
                                boxShadow: hoveredResidueId === r?.id
                                  ? '0 2px 8px rgba(99, 102, 241, 0.4)'
                                  : '0 1px 3px rgba(0,0,0,0.2)',
                                transform: hoveredResidueId === r?.id ? 'translateY(-1px)' : 'none'
                              }} 
                              title={`${r?.name || r?.longCode || ''} (Residue ${idx + 1})`}
                              onMouseEnter={(e) => {
                                // Visual hover effect (enhanced)
                                e.currentTarget.style.background = 'linear-gradient(135deg, rgba(99, 102, 241, 0.35), rgba(79, 70, 229, 0.35))'
                                e.currentTarget.style.borderColor = '#818cf8'
                                e.currentTarget.style.transform = 'translateY(-2px)'
                                e.currentTarget.style.boxShadow = '0 4px 12px rgba(99, 102, 241, 0.5)'
                                
                                // Highlight the residue in the viewer
                                const tname = currentAssetNameRef.current || 'PDBAsset'
                                if (r?.id !== undefined && r?.id !== null) {
                                  sendMessage({
                                    description: 'SetSelection',
                                    target: { name: tname },
                                    result: { residue: r.id }
                                  })
                                }
                              }}
                              onMouseLeave={(e) => {
                                // Reset visual style - explicitly set back to default values
                                const isHoveredInMolecule = hoveredResidueId === r?.id
                                e.currentTarget.style.background = isHoveredInMolecule
                                  ? 'linear-gradient(135deg, rgba(99, 102, 241, 0.3), rgba(79, 70, 229, 0.3))'
                                  : 'linear-gradient(135deg, rgba(31, 41, 55, 0.9), rgba(17, 24, 39, 0.9))'
                                e.currentTarget.style.borderColor = isHoveredInMolecule
                                  ? '#6366f1'
                                  : 'rgba(99, 102, 241, 0.3)'
                                e.currentTarget.style.transform = isHoveredInMolecule ? 'translateY(-1px)' : 'none'
                                e.currentTarget.style.boxShadow = isHoveredInMolecule
                                  ? '0 2px 8px rgba(99, 102, 241, 0.4)'
                                  : '0 1px 3px rgba(0,0,0,0.2)'
                                
                                // Clear the selection in the viewer
                                const tname = currentAssetNameRef.current || 'PDBAsset'
                                sendMessage({
                                  description: 'SetSelection',
                                  target: { name: tname },
                                  result: null // Clear selection
                                })
                              }}
                            >
                              {(r?.shortCode || r?.longCode || '?')}
                            </button>))
                          ))}
                        </div>
                      </>
                    )
                  })()}
                </div>
              )}
            </div>
          )}
          {showWorkspace && (
            <div style={{ position: 'absolute', top: `calc(${headerHeight()}px + 0.75em)`, right: 0, bottom: 0, maxWidth: '25%', background: '#0f1117', borderTop: '1px solid #2f3037', boxShadow: '0 -6px 20px rgba(0,0,0,0.3)', padding: 16, display: 'grid', gridTemplateRows: 'auto 1fr', gap: 12 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <strong style={{ color: '#e5e7eb', fontSize: 15 }}>Workspace</strong>
                <button 
                  onClick={async () => {
                    const state = await querySceneState();
                    setSceneState(state);
                  }}
                  style={{ padding: '6px 12px', borderRadius: 6, border: '1px solid #2f3037', background: '#1f2937', color: '#a5b4fc', cursor: 'pointer', fontSize: 12, transition: 'all 150ms ease' }}
                  onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.background = 'rgba(99,102,241,0.15)' }}
                  onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.background = '#1f2937' }}
                >
                  Refresh
                </button>
                <div style={{ flex: 1 }} />
                <CloseButton 
                  onClick={() => { setShowWorkspace(false); setShowOptions(false); setShowSettingsMenu(false); setShowSequence(false) }}
                  windowName='Workspace'
                  />
              </div>
              <div style={{ display: 'grid', gap: 10, overflow: "auto" }}>
                {sceneState?.assets && sceneState.assets.length > 0 ? (
                  // Only show primary assets (filter out viewer-generated clones)
                  sceneState.assets
                    .filter((asset: any) => asset.name === asset.baseAssetName)
                    .map((asset: any, primaryIdx: number) => {
                    const assetName = asset.name || `Asset ${primaryIdx + 1}`;
                    
                    // Parse the asset name to get source and ID
                    // Format: PDB_2BPS or CID_2244
                    let isPdb = assetName.startsWith('PDB_');
                    let isPubchem = assetName.startsWith('CID_');
                    let moleculeId = '';
                    
                    if (isPdb) {
                      moleculeId = assetName.substring(4); // Remove "PDB_"
                    } else if (isPubchem) {
                      moleculeId = assetName.substring(4); // Remove "CID_"
                    }
                    
                    let displayName = assetName;
                    let subtitle = '';
                    
                    if (isPdb) {
                      displayName = `PDB: ${moleculeId}`;
                      subtitle = 'Protein Data Bank';
                    } else if (isPubchem) {
                      displayName = `PubChem CID: ${moleculeId}`;
                      subtitle = 'PubChem Compound';
                    }
                    
                    return (
                      <div 
                        key={assetName}
                        style={{ 
                          display: 'flex', 
                          alignItems: 'center', 
                          gap: 12, 
                          padding: '12px 14px', 
                          borderRadius: 10, 
                          border: '1px solid #2f3037', 
                          background: 'linear-gradient(135deg, rgba(20,22,28,0.98), rgba(14,16,20,0.98))',
                          transition: 'all 150ms ease',
                          height: "fit-content"
                        }}
                        onMouseEnter={(e) => { (e.currentTarget as HTMLDivElement).style.borderColor = 'rgba(99,102,241,0.3)' }}
                        onMouseLeave={(e) => { (e.currentTarget as HTMLDivElement).style.borderColor = '#2f3037' }}
                      >
                        <div style={{ flex: 1, marginRight: "16px" }}>
                          <div style={{ color: '#e5e7eb', fontWeight: 600, fontSize: 14 }}>
                            {displayName}
                          </div>
                          {subtitle && (
                            <div style={{ color: '#9ca3af', fontSize: 12, marginTop: 4 }}>
                              {subtitle}
                            </div>
                          )}
                        </div>
                        <button
                          onClick={async () => {
                            try {
                              await sendAndWait({ description: 'RemoveAsset', target: { name: assetName } });
                              // Remove from loadedAssets state by matching source and ID
                              const source = isPdb ? 'pdb' : isPubchem ? 'pubchem' : null;
                              if (source && moleculeId) {
                                setLoadedAssets(prev => prev.filter(asset => 
                                  !(asset.source === source && asset.id === moleculeId)
                                ));
                              }
                              setHoverInfo(null);
                              // Refresh scene state
                              const state = await querySceneState();
                              setSceneState(state);
                            } catch (err) {
                            }
                          }}
                          style={{ 
                            padding: '8px 14px', 
                            borderRadius: 8, 
                            border: '1px solid rgba(239,68,68,0.3)', 
                            background: 'rgba(239,68,68,0.1)', 
                            color: '#ef4444', 
                            cursor: 'pointer', 
                            fontSize: 12,
                            fontWeight: 600,
                            transition: 'all 150ms ease'
                          }}
                          onMouseEnter={(e) => { 
                            (e.currentTarget as HTMLButtonElement).style.background = 'rgba(239,68,68,0.2)';
                            (e.currentTarget as HTMLButtonElement).style.borderColor = 'rgba(239,68,68,0.5)';
                          }}
                          onMouseLeave={(e) => { 
                            (e.currentTarget as HTMLButtonElement).style.background = 'rgba(239,68,68,0.1)';
                            (e.currentTarget as HTMLButtonElement).style.borderColor = 'rgba(239,68,68,0.3)';
                          }}
                        >
                          Delete
                        </button>
                      </div>
                    );
                  })
                ) : (
                  <div style={{ 
                    color: '#9ca3af', 
                    fontSize: 14, 
                    textAlign: 'center', 
                    padding: '40px 20px',
                    border: '1px dashed #2f3037',
                    borderRadius: 10
                  }}>
                    No molecules in workspace. Load a molecule to get started.
                  </div>
                )}
              </div>
            </div>
          )}
          {/* Backend logs overlay removed per request; logs are now streamed to console */}
          
          {/* Centered prompt box for first-time users */}
          {firstTimeUser && introComplete && (
            <div
              style={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                width: '90%',
                maxWidth: 600,
                borderRadius: 24,
                border: '3px solid rgba(99, 102, 241, 0.8)',
                zIndex: 200,
                animation: 'fadeInUp 0.6s cubic-bezier(0.34, 1.56, 0.64, 1) 1.8s forwards, borderGlow 2s ease-in-out infinite',
                opacity: 0,
                boxShadow: '0 0 30px rgba(99, 102, 241, 0.6), 0 0 60px rgba(139, 92, 246, 0.4), inset 0 0 20px rgba(99, 102, 241, 0.1)',
                overflow: 'hidden'
              }}
            >
              {/* Background with animated image */}
              <div style={{
                position: 'absolute',
                inset: 0,
                borderRadius: 24,
                overflow: 'hidden',
                zIndex: 0
              }}>
                <div
                  style={{
                    position: 'absolute',
                    inset: '-10%',
                    backgroundImage: `url(${moleculeBackground})`,
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                    animation: 'molViewerDollyZoom 20s ease-in-out infinite'
                  }}
                />
                <div style={{
                  position: 'absolute',
                  inset: 0,
                  background: 'rgba(0, 0, 0, 0.7)'
                }} />
              </div>
              
              {/* Content */}
              <div style={{
                position: 'relative',
                zIndex: 1,
                padding: '40px 48px'
              }}>
              {/* Header with lightning bolt icon */}
              <div style={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: 16, 
                marginBottom: 24,
                justifyContent: 'center'
              }}>
                <svg width="48" height="48" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ animation: 'pulse 2s ease-in-out infinite' }}>
                  <path d="M13 2L3 14h8l-1 8 10-12h-8l1-8z" fill="white"/>
                </svg>
                <h2 style={{ 
                  margin: 0, 
                  fontSize: 28, 
                  fontWeight: 700,
                  background: 'linear-gradient(135deg, #a5b4fc, #c7d2fe)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text'
                }}>
                  Snorkle Molecular Viewer
                </h2>
              </div>

              {/* Welcome text */}
              <p style={{ 
                color: '#e5e9f5', 
                fontSize: 16, 
                lineHeight: 1.6,
                marginBottom: 16,
                textAlign: 'center',
                opacity: 0.9
              }}>
                Assisted by natural language processing
              </p>

              {/* Tutorial Links */}
              <div style={{ 
                display: 'flex', 
                gap: 16, 
                justifyContent: 'center', 
                marginBottom: 24,
                flexWrap: 'wrap'
              }}>
                <YouTubeVideoPopup 
                  videoUrl="https://youtu.be/hfY2y3XGLmI"
                  linkText="Getting Started"
                  variant="text"
                />
                <YouTubeVideoPopup 
                  videoUrl="https://youtu.be/lcmoQtw3KaA"
                  linkText="Importing from PyMol"
                  variant="text"
                />
              </div>

              {/* Prompt input */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                {/* PyMOL Mode Toggle */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <label style={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: 10, 
                    cursor: 'pointer',
                    fontSize: 14,
                    color: pymolMode ? '#6366f1' : '#9ca3af',
                    fontWeight: pymolMode ? 600 : 500,
                    transition: 'all 200ms ease'
                  }}>
                    <input
                      type="checkbox"
                      checked={pymolMode}
                      onChange={(e) => setPymolMode(e.target.checked)}
                      style={{ 
                        width: 18, 
                        height: 18, 
                        accentColor: '#6366f1',
                        cursor: 'pointer'
                      }}
                    />
                    <span>PyMOL Mode (Lite)</span>
                  </label>
                  <button
                    onClick={() => setShowPymolHelp(true)}
                    style={{
                      background: 'none',
                      border: 'none',
                      cursor: 'pointer',
                      padding: 2,
                      color: '#6b7280',
                      fontSize: 16,
                      display: 'flex',
                      alignItems: 'center',
                      transition: 'color 200ms'
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.color = '#6366f1'}
                    onMouseLeave={(e) => e.currentTarget.style.color = '#6b7280'}
                    title="PyMOL Commands Help"
                  >
                    ⓘ
                  </button>
                </div>
                <input
                  ref={welcomeInputRef}
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault()
                      handleSubmit()
                    } else if (e.key === 'ArrowUp') {
                      e.preventDefault()
                      if (commandHistory.length === 0) return
                      const newIndex = historyIndex === -1 ? commandHistory.length - 1 : Math.max(0, historyIndex - 1)
                      setHistoryIndex(newIndex)
                      setInput(commandHistory[newIndex])
                    } else if (e.key === 'ArrowDown') {
                      e.preventDefault()
                      if (historyIndex === -1) return
                      const newIndex = historyIndex + 1
                      if (newIndex >= commandHistory.length) {
                        setHistoryIndex(-1)
                        setInput('')
                      } else {
                        setHistoryIndex(newIndex)
                        setInput(commandHistory[newIndex])
                      }
                    }
                  }}
                  placeholder={pymolMode ? "Enter PyMOL commands (e.g. 'fetch 1ubq')" : "Try: 'Show ubiquitin' or 'Display caffeine molecule'"}
                  autoFocus
                  style={{ 
                    padding: '16px 20px',
                    borderRadius: 12,
                    border: pymolMode ? '2px solid rgba(99, 102, 241, 0.5)' : '2px solid rgba(99, 102, 241, 0.3)',
                    background: pymolMode ? 'rgba(99, 102, 241, 0.1)' : 'rgba(19, 20, 26, 0.7)',
                    color: '#e5e7eb',
                    fontSize: 16,
                    outline: 'none',
                    transition: 'all 300ms cubic-bezier(0.4, 0, 0.2, 1)',
                    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.2)'
                  }}
                  onFocus={(e) => {
                    e.currentTarget.style.borderColor = 'rgba(99, 102, 241, 0.6)'
                    e.currentTarget.style.boxShadow = '0 4px 20px rgba(99, 102, 241, 0.3)'
                  }}
                  onBlur={(e) => {
                    e.currentTarget.style.borderColor = 'rgba(99, 102, 241, 0.3)'
                    e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.2)'
                  }}
                />
                <button
                  onClick={handleSubmit}
                  disabled={working}
                  style={{ 
                    padding: '16px 20px',
                    borderRadius: 12,
                    border: 'none',
                    background: working ? '#2a2b31' : 'linear-gradient(135deg, #6366f1, #4f46e5)',
                    color: 'white',
                    cursor: working ? 'default' : 'pointer',
                    fontWeight: 700,
                    fontSize: 16,
                    transition: 'all 200ms cubic-bezier(0.4, 0, 0.2, 1)',
                    boxShadow: working ? 'none' : '0 6px 20px rgba(99, 102, 241, 0.4)',
                    letterSpacing: '0.5px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    minHeight: 56
                  }}
                  onMouseEnter={(e) => {
                    if (!working) {
                      e.currentTarget.style.transform = 'translateY(-2px)'
                      e.currentTarget.style.boxShadow = '0 8px 28px rgba(99, 102, 241, 0.5)'
                    }
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'translateY(0)'
                    e.currentTarget.style.boxShadow = working ? 'none' : '0 6px 20px rgba(99, 102, 241, 0.4)'
                  }}
                >
                  {working ? 'Thinking...' : 'Get Started ✨'}
                </button>
              </div>

              {/* Example prompts */}
              <div style={{ 
                marginTop: 28, 
                paddingTop: 24, 
                borderTop: '1px solid rgba(99, 102, 241, 0.2)'
              }}>
                <p style={{ 
                  color: '#a5b4fc', 
                  fontSize: 13, 
                  marginBottom: 12,
                  fontWeight: 600,
                  letterSpacing: '0.5px',
                  textTransform: 'uppercase'
                }}>
                  Popular examples
                </p>
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  {['Ubiquitin', 'Insulin', 'Caffeine', 'Aspirin'].map((example) => (
                    <button
                      key={example}
                      onClick={() => {
                        setInput(`Show ${example}`)
                      }}
                      style={{
                        padding: '8px 16px',
                        borderRadius: 8,
                        border: '1px solid rgba(99, 102, 241, 0.3)',
                        background: 'rgba(99, 102, 241, 0.1)',
                        color: '#c7d2fe',
                        fontSize: 13,
                        cursor: 'pointer',
                        transition: 'all 150ms ease',
                        fontWeight: 500
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.background = 'rgba(99, 102, 241, 0.2)'
                        e.currentTarget.style.borderColor = 'rgba(99, 102, 241, 0.5)'
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.background = 'rgba(99, 102, 241, 0.1)'
                        e.currentTarget.style.borderColor = 'rgba(99, 102, 241, 0.3)'
                      }}
                    >
                      {example}
                    </button>
                  ))}
                </div>
              </div>
              
              {/* Mobile disclaimer */}
              <p style={{
                fontSize: 11,
                color: '#9ca3af',
                marginTop: 20,
                marginBottom: 0,
                textAlign: 'center',
                opacity: 0.8
              }}>
                This feature is best used on desktop. Mobile support is limited.
              </p>
              </div>
            </div>
          )}
        </main>
      </div>

      {/* PyMOL Help Documentation Popup */}
      {showPymolHelp && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.8)',
            backdropFilter: 'blur(4px)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 10000,
            padding: 20
          }}
          onClick={() => setShowPymolHelp(false)}
        >
          <div
            style={{
              background: 'linear-gradient(135deg, #1e1b4b 0%, #312e81 100%)',
              borderRadius: 16,
              maxWidth: 800,
              maxHeight: '90vh',
              width: '100%',
              overflow: 'hidden',
              display: 'flex',
              flexDirection: 'column',
              boxShadow: '0 20px 60px rgba(0, 0, 0, 0.5)',
              border: '1px solid rgba(99, 102, 241, 0.3)'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div style={{
              padding: '24px 32px',
              borderBottom: '1px solid rgba(99, 102, 241, 0.3)',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              background: 'rgba(0, 0, 0, 0.2)'
            }}>
              <div>
                <h2 style={{ margin: 0, color: '#e0e7ff', fontSize: 24, fontWeight: 700 }}>
                  PyMOL Mode (Lite)
                </h2>
                <p style={{ margin: '8px 0 0 0', color: '#a5b4fc', fontSize: 14 }}>
                  Supported commands and syntax
                </p>
              </div>
              <button
                onClick={() => setShowPymolHelp(false)}
                style={{
                  background: 'rgba(99, 102, 241, 0.2)',
                  border: '1px solid rgba(99, 102, 241, 0.4)',
                  borderRadius: 8,
                  padding: '8px 16px',
                  color: '#e0e7ff',
                  cursor: 'pointer',
                  fontSize: 14,
                  fontWeight: 600,
                  transition: 'all 200ms'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = 'rgba(99, 102, 241, 0.3)'
                  e.currentTarget.style.borderColor = 'rgba(99, 102, 241, 0.6)'
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'rgba(99, 102, 241, 0.2)'
                  e.currentTarget.style.borderColor = 'rgba(99, 102, 241, 0.4)'
                }}
              >
                Close
              </button>
            </div>

            {/* Content */}
            <div style={{
              padding: '24px 32px',
              overflowY: 'auto',
              flex: 1
            }}>
              {/* Overview */}
              <div style={{ marginBottom: 24, padding: 16, background: 'rgba(99, 102, 241, 0.1)', borderRadius: 8, border: '1px solid rgba(99, 102, 241, 0.3)' }}>
                <p style={{ margin: 0, color: '#c7d2fe', fontSize: 14, lineHeight: 1.6 }}>
                  PyMOL Mode (Lite) supports a subset of PyMOL commands for molecular visualization. 
                  Commands are parsed and translated to viewer operations. Some advanced features are not available.
                </p>
              </div>

              {/* Command Categories */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                {/* Display Commands */}
                <CommandCategory
                  title="Display & Representation"
                  commands={[
                    { name: 'show', syntax: 'show <representation>', examples: ['show cartoon', 'show surface', 'show sticks', 'show spheres', 'show backbone', 'show putty', 'show mesh'], description: 'Display molecule in specified representation' },
                    { name: 'hide', syntax: 'hide <target>', examples: ['hide everything', 'hide surface', 'hide ligands'], description: 'Hide molecule or specific components' },
                    { name: 'as', syntax: 'as <representation>', examples: ['as cartoon', 'as sticks', 'as spheres'], description: 'Quick representation change (shorthand for show)' },
                  ]}
                />

                {/* Color Commands */}
                <CommandCategory
                  title="Coloring"
                  commands={[
                    { name: 'color', syntax: 'color <name> | color by <scheme>', examples: ['color red', 'color by chain', 'color by element', 'color by hydrophobicity'], description: 'Apply uniform color or color scheme' },
                    { name: 'spectrum', syntax: 'spectrum <scheme>', examples: ['spectrum chain', 'spectrum element', 'spectrum rainbow', 'spectrum hydrophobicity'], description: 'Apply color spectrum/scheme' },
                  ]}
                />

                {/* Selection Commands */}
                <CommandCategory
                  title="Selection"
                  commands={[
                    { name: 'select', syntax: 'select <selection>', examples: ['select chain A', 'select resi 10', 'select resi 10-20', 'select atom 42', 'select none'], description: 'Select specific atoms, residues, or chains' },
                  ]}
                />

                {/* Camera Commands */}
                <CommandCategory
                  title="Camera & View"
                  commands={[
                    { name: 'orient', syntax: 'orient', examples: ['orient'], description: 'Reset camera to default view' },
                    { name: 'reset', syntax: 'reset', examples: ['reset'], description: 'Reset camera (alias for orient)' },
                    { name: 'zoom', syntax: 'zoom [selection]', examples: ['zoom', 'zoom chain A', 'zoom resi 10'], description: 'Zoom camera to selection' },
                    { name: 'center', syntax: 'center [selection]', examples: ['center', 'center chain A'], description: 'Center view on selection' },
                    { name: 'rock', syntax: 'rock [0|1]', examples: ['rock', 'rock 0'], description: 'Start/stop auto-rotation (use "set sweep_speed" to control speed)' },
                  ]}
                />

                {/* Settings Commands */}
                <CommandCategory
                  title="Settings"
                  commands={[
                    { name: 'set', syntax: 'set <property>, <value>', examples: ['set stick_radius, 0.5', 'set transparency, 0.3', 'set surface_color, red', 'set shininess, 50', 'set surface_mode, 1', 'set sweep_speed, 10'], description: 'Adjust molecular properties and orbit speed (sweep_speed: 0-30 seconds)' },
                    { name: 'bg_color', syntax: 'bg_color <color>', examples: ['bg_color black', 'bg_color white', 'bg_color gray', 'bg_color snorkle'], description: 'Set background color (use "snorkle" for default gradient)' },
                  ]}
                />

                {/* Scene Management */}
                <CommandCategory
                  title="Scene Management"
                  commands={[
                    { name: 'fetch', syntax: 'fetch <pdb_id> | fetch cid:<cid> | fetch pubchem:<name>', examples: ['fetch 1ubq', 'fetch 2hhb', 'fetch cid:2244', 'fetch pubchem:aspirin', 'fetch pubchem:caffeine'], description: 'Load PDB structure from RCSB or PubChem compound by CID or name' },
                    { name: 'delete', syntax: 'delete [all|name]', examples: ['delete', 'delete all'], description: 'Remove molecule from scene' },
                    { name: 'enable', syntax: 'enable [name]', examples: ['enable'], description: 'Make molecule visible' },
                    { name: 'disable', syntax: 'disable [name]', examples: ['disable'], description: 'Make molecule invisible' },
                  ]}
                />
              </div>

              {/* Limitations */}
              <div style={{ marginTop: 32, padding: 16, background: 'rgba(239, 68, 68, 0.1)', borderRadius: 8, border: '1px solid rgba(239, 68, 68, 0.3)' }}>
                <h3 style={{ margin: '0 0 12px 0', color: '#fca5a5', fontSize: 16, fontWeight: 600 }}>
                  Limitations
                </h3>
                <ul style={{ margin: 0, paddingLeft: 20, color: '#fecaca', fontSize: 13, lineHeight: 1.8 }}>
                  <li>Measurement commands (distance, angle, dihedral) require position data</li>
                  <li>Label positioning is not available through messaging protocol</li>
                  <li>Camera rotation (turn) requires complex calculations</li>
                  <li>Some PyMOL settings have no direct viewer equivalent</li>
                  <li>Atom-level editing commands not supported</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete Molecule Confirmation Modal */}
      {showDeleteConfirm && moleculeToDelete && (
        <div style={{
          position: 'fixed',
          inset: 0,
          background: 'rgba(0, 0, 0, 0.7)',
          backdropFilter: 'blur(4px)',
          WebkitBackdropFilter: 'blur(4px)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
          padding: 20
        }} onClick={() => setShowDeleteConfirm(false)}>
          <div style={{
            background: 'linear-gradient(135deg, rgba(17, 24, 39, 0.98), rgba(31, 41, 55, 0.98))',
            borderRadius: 16,
            border: '1px solid rgba(239, 68, 68, 0.5)',
            boxShadow: '0 20px 60px rgba(0, 0, 0, 0.6)',
            maxWidth: 500,
            width: '100%',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden'
          }} onClick={(e) => e.stopPropagation()}>
            {/* Modal Header */}
            <div style={{
              padding: '20px 24px',
              borderBottom: '1px solid rgba(239, 68, 68, 0.3)',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              background: 'rgba(0, 0, 0, 0.3)'
            }}>
              <h3 style={{ margin: 0, color: '#f0f4ff', fontSize: 18, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 10 }}>
                <span>🗑️</span>
                <span>Delete Molecule</span>
              </h3>
              <button
                onClick={() => setShowDeleteConfirm(false)}
                style={{
                  width: 32,
                  height: 32,
                  borderRadius: 8,
                  border: '1px solid rgba(239, 68, 68, 0.3)',
                  background: 'rgba(30, 32, 42, 0.6)',
                  color: '#ef4444',
                  fontSize: 20,
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  transition: 'all 150ms ease'
                }}
                onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(239,68,68,0.15)' }}
                onMouseLeave={(e) => { e.currentTarget.style.background = 'rgba(30,32,42,0.6)' }}
              >
                ×
              </button>
            </div>

            {/* Modal Body */}
            <div style={{ padding: '24px' }}>
              <p style={{ margin: '0 0 20px 0', color: '#e5e9f5', fontSize: 15, lineHeight: 1.6 }}>
                Are you sure you want to delete <strong style={{ color: '#ef4444' }}>{moleculeToDelete}</strong> from the workspace?
              </p>
              <p style={{ margin: '0 0 24px 0', color: '#9ca3af', fontSize: 13 }}>
                This action cannot be undone.
              </p>

              {/* Action Buttons */}
              <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end' }}>
                <button
                  onClick={() => setShowDeleteConfirm(false)}
                  style={{
                    padding: '10px 20px',
                    borderRadius: 8,
                    border: '1px solid rgba(99, 102, 241, 0.5)',
                    background: 'rgba(99, 102, 241, 0.2)',
                    color: '#a5b4fc',
                    cursor: 'pointer',
                    fontSize: 14,
                    fontWeight: 600,
                    transition: 'all 150ms ease'
                  }}
                  onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(99,102,241,0.3)' }}
                  onMouseLeave={(e) => { e.currentTarget.style.background = 'rgba(99,102,241,0.2)' }}
                >
                  Cancel
                </button>
                <button
                  onClick={async () => {
                    try {
                      // Remove from viewer
                      await sendAndWait({ description: 'RemoveAsset', target: { name: moleculeToDelete } })
                      
                      // Parse molecule name to extract source and ID
                      const isPdb = moleculeToDelete.startsWith('PDB_')
                      const isPubchem = moleculeToDelete.startsWith('CID_')
                      
                      if (isPdb) {
                        const id = moleculeToDelete.substring(4).toUpperCase()
                        setLoadedAssets(prev => prev.filter(asset => !(asset.source === 'pdb' && asset.id === id)))
                      } else if (isPubchem) {
                        const id = moleculeToDelete.substring(4)
                        setLoadedAssets(prev => prev.filter(asset => !(asset.source === 'pubchem' && asset.id === id)))
                      }
                      
                      // Clear active molecule if it was the deleted one
                      if (activeMolecule === moleculeToDelete) {
                        setActiveMolecule(null)
                        currentAssetNameRef.current = 'PDBAsset'
                      }
                      
                      // Refresh scene state
                      await querySceneState()
                      
                    } catch (err) {
                    } finally {
                      setShowDeleteConfirm(false)
                      setMoleculeToDelete(null)
                    }
                  }}
                  style={{
                    padding: '10px 20px',
                    borderRadius: 8,
                    border: '1px solid rgba(239, 68, 68, 0.5)',
                    background: 'rgba(239, 68, 68, 0.2)',
                    color: '#ef4444',
                    cursor: 'pointer',
                    fontSize: 14,
                    fontWeight: 600,
                    transition: 'all 150ms ease'
                  }}
                  onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(239,68,68,0.3)' }}
                  onMouseLeave={(e) => { e.currentTarget.style.background = 'rgba(239,68,68,0.2)' }}
                >
                  Delete
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Report Inaccuracies Modal */}
      {showReportModal && (
        <div style={{
          position: 'fixed',
          inset: 0,
          background: 'rgba(0, 0, 0, 0.7)',
          backdropFilter: 'blur(4px)',
          WebkitBackdropFilter: 'blur(4px)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
          padding: 20
        }} onClick={() => !reportSubmitting && setShowReportModal(false)}>
          <div style={{
            background: 'linear-gradient(135deg, rgba(17, 24, 39, 0.98), rgba(31, 41, 55, 0.98))',
            borderRadius: 16,
            border: '1px solid rgba(99, 102, 241, 0.3)',
            boxShadow: '0 20px 60px rgba(0, 0, 0, 0.6)',
            maxWidth: 700,
            width: '100%',
            maxHeight: '90vh',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden'
          }} onClick={(e) => e.stopPropagation()}>
            {/* Modal Header */}
            <div style={{
              padding: '20px 24px',
              borderBottom: '1px solid rgba(99, 102, 241, 0.2)',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              background: 'rgba(0, 0, 0, 0.3)'
            }}>
              <h3 style={{ margin: 0, color: '#f0f4ff', fontSize: 18, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 10 }}>
                <span>🚩</span>
                <span>Report AI Chat Inaccuracies</span>
              </h3>
              <button
                onClick={() => !reportSubmitting && setShowReportModal(false)}
                disabled={reportSubmitting}
                style={{
                  width: 32,
                  height: 32,
                  borderRadius: 8,
                  border: '1px solid rgba(99, 102, 241, 0.3)',
                  background: 'rgba(30, 32, 42, 0.6)',
                  color: '#a5b4fc',
                  fontSize: 20,
                  cursor: reportSubmitting ? 'not-allowed' : 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  opacity: reportSubmitting ? 0.5 : 1
                }}
              >
                ×
              </button>
            </div>

            {/* Modal Body */}
            <div style={{ padding: '20px 24px', overflow: 'auto', flex: 1 }}>
              {reportStatus.success ? (
                <div style={{ textAlign: 'center', padding: '40px 20px' }}>
                  <div style={{ fontSize: 48, marginBottom: 16 }}>✅</div>
                  <h4 style={{ color: '#f0f4ff', marginBottom: 12 }}>Report Submitted</h4>
                  <p style={{ color: '#9ca3af', fontSize: 14 }}>
                    Thank you for your feedback. We'll review the chat history and address any issues.
                  </p>
                </div>
              ) : (
                <>
                  {/* Privacy Notice */}
                  <div style={{
                    padding: '12px 16px',
                    background: 'rgba(99, 102, 241, 0.1)',
                    border: '1px solid rgba(99, 102, 241, 0.3)',
                    borderRadius: 8,
                    marginBottom: 20
                  }}>
                    <p style={{ margin: 0, color: '#c7d2fe', fontSize: 13, lineHeight: 1.6 }}>
                      <strong>🔒 Privacy Notice:</strong> Chat content will be anonymized before submission. 
                      Only the conversation and your optional comment will be sent to help us improve AI accuracy.
                    </p>
                  </div>

                  {/* Optional Comment */}
                  <div style={{ marginBottom: 20 }}>
                    <label style={{
                      display: 'block',
                      color: '#e5e9f5',
                      fontSize: 14,
                      fontWeight: 600,
                      marginBottom: 8
                    }}>
                      Additional Comments (Optional)
                    </label>
                    <textarea
                      value={reportComment}
                      onChange={(e) => setReportComment(e.target.value)}
                      placeholder="Describe the issue or provide additional context..."
                      disabled={reportSubmitting}
                      style={{
                        width: '100%',
                        minHeight: 100,
                        padding: '12px',
                        borderRadius: 8,
                        border: '1px solid rgba(99, 102, 241, 0.3)',
                        background: 'rgba(0, 0, 0, 0.3)',
                        color: '#e5e9f5',
                        fontSize: 14,
                        fontFamily: 'inherit',
                        resize: 'vertical',
                        outline: 'none'
                      }}
                    />
                  </div>

                  {/* Chat History Preview */}
                  <div style={{ marginBottom: 20 }}>
                    <label style={{
                      display: 'block',
                      color: '#e5e9f5',
                      fontSize: 14,
                      fontWeight: 600,
                      marginBottom: 8
                    }}>
                      Chat History ({messages.length} messages)
                    </label>
                    <div style={{
                      maxHeight: 200,
                      overflow: 'auto',
                      padding: '12px',
                      borderRadius: 8,
                      border: '1px solid rgba(99, 102, 241, 0.3)',
                      background: 'rgba(0, 0, 0, 0.3)'
                    }}>
                      {messages.length === 0 ? (
                        <p style={{ margin: 0, color: '#9ca3af', fontSize: 13, fontStyle: 'italic' }}>
                          No messages yet
                        </p>
                      ) : (
                        messages.map((msg, idx) => (
                          <div key={idx} style={{
                            marginBottom: 12,
                            paddingBottom: 12,
                            borderBottom: idx < messages.length - 1 ? '1px solid rgba(99, 102, 241, 0.1)' : 'none'
                          }}>
                            <div style={{
                              color: msg.role === 'user' ? '#60a5fa' : '#a78bfa',
                              fontSize: 11,
                              fontWeight: 600,
                              marginBottom: 4,
                              textTransform: 'uppercase'
                            }}>
                              {msg.role}
                            </div>
                            <div style={{
                              color: '#e5e9f5',
                              fontSize: 13,
                              lineHeight: 1.5,
                              whiteSpace: 'pre-wrap',
                              wordBreak: 'break-word'
                            }}>
                              {msg.content}
                            </div>
                          </div>
                        ))
                      )}
                    </div>
                  </div>

                  {/* Workspace Info */}
                  <div style={{ marginBottom: 20 }}>
                    <label style={{
                      display: 'block',
                      color: '#e5e9f5',
                      fontSize: 14,
                      fontWeight: 600,
                      marginBottom: 8
                    }}>
                      Current Workspace
                    </label>
                    <div style={{
                      padding: '12px',
                      borderRadius: 8,
                      border: '1px solid rgba(99, 102, 241, 0.3)',
                      background: 'rgba(0, 0, 0, 0.3)',
                      color: '#9ca3af',
                      fontSize: 13
                    }}>
                      {loadedAssets.length === 0 ? (
                        <span style={{ fontStyle: 'italic' }}>No molecules loaded</span>
                      ) : (
                        <div>
                          {loadedAssets.map((asset, idx) => (
                            <div key={idx} style={{ marginBottom: 4 }}>
                              • {asset.source.toUpperCase()}: {asset.id}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Error Message */}
                  {reportStatus.error && (
                    <div style={{
                      padding: '12px 16px',
                      background: 'rgba(239, 68, 68, 0.1)',
                      border: '1px solid rgba(239, 68, 68, 0.3)',
                      borderRadius: 8,
                      marginBottom: 20,
                      color: '#fca5a5',
                      fontSize: 13
                    }}>
                      {reportStatus.error}
                    </div>
                  )}

                  {/* Submit Button */}
                  <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end' }}>
                    <button
                      onClick={() => setShowReportModal(false)}
                      disabled={reportSubmitting}
                      style={{
                        padding: '10px 20px',
                        borderRadius: 8,
                        border: '1px solid rgba(99, 102, 241, 0.3)',
                        background: 'rgba(30, 32, 42, 0.6)',
                        color: '#a5b4fc',
                        cursor: reportSubmitting ? 'not-allowed' : 'pointer',
                        fontSize: 14,
                        fontWeight: 500,
                        opacity: reportSubmitting ? 0.5 : 1
                      }}
                    >
                      Cancel
                    </button>
                    <button
                      onClick={async () => {
                        setReportSubmitting(true)
                        setReportStatus({})

                        // Format chat history
                        const chatHistory = messages.map((msg, idx) => 
                          `[${idx + 1}] ${msg.role.toUpperCase()}: ${msg.content}`
                        ).join('\n\n')

                        // Format workspace info
                        const workspaceInfo = loadedAssets.length === 0 
                          ? 'No molecules loaded'
                          : loadedAssets.map(asset => `${asset.source.toUpperCase()}: ${asset.id}`).join(', ')

                        // Compose email body
                        const emailBody = `User Comments:\n${reportComment || 'No additional comments provided'}\n\n========================================\nCURRENT WORKSPACE:\n========================================\n${workspaceInfo}\n\n========================================\nCHAT HISTORY (${messages.length} messages):\n========================================\n\n${chatHistory || 'No chat history'}`

                        APIContact(
                          {
                            email: 'support@snorkle.io',  // Anonymous report from support
                            comment: emailBody,
                            header: 'Molecular Viewer AI - Inaccuracy Report'
                          },
                          () => {
                            setReportSubmitting(false)
                            setReportStatus({ success: true })
                            setTimeout(() => {
                              setShowReportModal(false)
                              setReportComment('')
                              setReportStatus({})
                            }, 2500)
                          },
                          (err: any) => {
                            setReportSubmitting(false)
                            setReportStatus({ error: typeof err === 'string' ? err : 'Failed to submit report. Please try again.' })
                          }
                        )
                      }}
                      disabled={reportSubmitting}
                      style={{
                        padding: '10px 20px',
                        borderRadius: 8,
                        border: '1px solid rgba(239, 68, 68, 0.5)',
                        background: reportSubmitting ? 'rgba(100, 100, 100, 0.5)' : 'rgba(239, 68, 68, 0.2)',
                        color: reportSubmitting ? '#9ca3af' : '#ef4444',
                        cursor: reportSubmitting ? 'not-allowed' : 'pointer',
                        fontSize: 14,
                        fontWeight: 600
                      }}
                    >
                      {reportSubmitting ? 'Submitting...' : 'Submit Report'}
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  )
}

// Command Category Component
function CommandCategory({ title, commands }: { title: string; commands: Array<{ name: string; syntax: string; examples: string[]; description: string }> }) {
  const [expanded, setExpanded] = React.useState<Record<string, boolean>>({})

  return (
    <div style={{
      background: 'rgba(30, 27, 75, 0.4)',
      borderRadius: 12,
      border: '1px solid rgba(99, 102, 241, 0.2)',
      overflow: 'hidden'
    }}>
      <h3 style={{
        margin: 0,
        padding: '16px 20px',
        color: '#a5b4fc',
        fontSize: 16,
        fontWeight: 600,
        borderBottom: '1px solid rgba(99, 102, 241, 0.2)',
        background: 'rgba(0, 0, 0, 0.2)'
      }}>
        {title}
      </h3>
      <div style={{ padding: '8px 0' }}>
        {commands.map((cmd) => (
          <div key={cmd.name} style={{ borderBottom: '1px solid rgba(99, 102, 241, 0.1)' }}>
            <button
              onClick={() => setExpanded({ ...expanded, [cmd.name]: !expanded[cmd.name] })}
              style={{
                width: '100%',
                padding: '12px 20px',
                background: expanded[cmd.name] ? 'rgba(99, 102, 241, 0.1)' : 'transparent',
                border: 'none',
                color: '#e0e7ff',
                fontSize: 14,
                fontWeight: 600,
                cursor: 'pointer',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                transition: 'all 150ms',
                fontFamily: 'monospace'
              }}
              onMouseEnter={(e) => {
                if (!expanded[cmd.name]) {
                  e.currentTarget.style.background = 'rgba(99, 102, 241, 0.05)'
                }
              }}
              onMouseLeave={(e) => {
                if (!expanded[cmd.name]) {
                  e.currentTarget.style.background = 'transparent'
                }
              }}
            >
              <span>{cmd.name}</span>
              <span style={{ fontSize: 12 }}>{expanded[cmd.name] ? '▼' : '▶'}</span>
            </button>
            {expanded[cmd.name] && (
              <div style={{ padding: '16px 20px', background: 'rgba(0, 0, 0, 0.2)' }}>
                <p style={{ margin: '0 0 12px 0', color: '#c7d2fe', fontSize: 13, lineHeight: 1.6 }}>
                  {cmd.description}
                </p>
                <div style={{ marginBottom: 12 }}>
                  <div style={{ fontSize: 11, color: '#a5b4fc', marginBottom: 6, fontWeight: 600 }}>SYNTAX:</div>
                  <code style={{
                    display: 'block',
                    padding: '8px 12px',
                    background: 'rgba(0, 0, 0, 0.3)',
                    borderRadius: 6,
                    color: '#fbbf24',
                    fontSize: 13,
                    fontFamily: 'monospace',
                    border: '1px solid rgba(251, 191, 36, 0.2)'
                  }}>
                    {cmd.syntax}
                  </code>
                </div>
                <div>
                  <div style={{ fontSize: 11, color: '#a5b4fc', marginBottom: 6, fontWeight: 600 }}>EXAMPLES:</div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                    {cmd.examples.map((ex, i) => (
                      <code key={i} style={{
                        display: 'block',
                        padding: '6px 10px',
                        background: 'rgba(0, 0, 0, 0.3)',
                        borderRadius: 4,
                        color: '#34d399',
                        fontSize: 12,
                        fontFamily: 'monospace',
                        border: '1px solid rgba(52, 211, 153, 0.2)'
                      }}>
                        {ex}
                      </code>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  )
}

export default MolecularViewerApp



